// 装備スキル catalogue. ADR 0015 §4.6 / §3.1。
//
// 9 シナジー軸 × 約 400 個の母集団 (ADR 0012 §2.2 / `.context/c5c_plan.md` v16 §B)。
// 本ファイルは template 群 (Skill object) を `ITEM_SKILLS` として export する。
// 装備に乗る `ItemSkill` instance (rolled `values` + `tier`) は別経路 (装備生成 / drop、
// 別 ADR スコープ)。
//
// 命名規約 (ADR 0012 plan v16 §A、§J #1):
//   - 共通: `item.common.<archetype>.<keyword>` (例: `item.common.vanilla-element-strike.fire`)
//   - ユニーク: `item.unique.<themeId>.<keyword>` (例: `item.unique.juggernaut.vanilla-amp`)
//
// 投入順 (ADR 0015 §4.6):
//   - Step 6.1 軸 6 純素 (30 個) ← 本 commit
//   - Step 6.2 軸 9 キル連鎖 (32 個)
//   - Step 6.3 軸 1 属性スタッキング (75 個)
//   - Step 6.4 軸 2 状態異常 (73 個)
//   - Step 6.5 軸 7 資源循環 (26 個)
//   - Step 6.6 軸 8 射程・位置 (28 個)
//   - Step 6.7 軸 5 行動ルール (31 個)
//   - Step 6.8 軸 3 設置物 (52 個)
//   - Step 6.9 軸 4 召喚 (53 個)
//   - Step 6.10 Summoner 補完 unique (4 個)
//
// `Skill` template は ItemSkill 個体差 (`values` / `tier`) を持たない。Tier 個体差表は
// `item-skills.test.ts` 内 fixture (`TIER_VALUES_FIXTURE`) で保持し、§J #4 単調性 +
// §J #13 values スキーマ網羅で検証する (ADR 0015 §6 / 計画 §6.3)。
//
// kind 選定ルール (ADR 0013 §2.2.1):
//   - reactive 必須: onTakenDamage / onHpBelow / onShieldBreak / onAttacked /
//     onAllyHpBelow / onSelfDebuffed (被害イベント / HP・シールド閾値)
//   - trigger: 上記以外 (onCrit / onHit / onKill / onMarkBurst / onStatus* /
//     onPlaceable* / onSummon*)
// 各軸 catalogue Skill object の kind 選定は本ルールに従う。
import { seconds, } from '@renavora/shared';
/** Order pinned to keep catalogue iteration deterministic. */
const ELEMENTS_7 = [
    'physical',
    'fire',
    'ice',
    'lightning',
    'earth',
    'light',
    'dark',
];
/** `mark.<element>` StatusEffectId 引き出し。軸 1 archetype 1/2/3/7/8 で使用。 */
function markId(element) {
    return `mark.${element}`;
}
// =============================================================================
// Step 6.1 軸 6 純素 (`.context/c5c_vanilla_axis.md` v1)
//
// 30 個 = common 25 + unique 5。シナジー条件なし、装備したら確実に効く床上げ役。
// archetype 別:
//   1. 素属性 active × 7 (全 7 属性、無条件発動)
//   2. 素 passive ステ加算 × 6 (atk-bonus / crit-rate / crit-dmg / armor / hp-max /
//      status-resist、`stat-boost.<type>` status 経由)
//   3. 素 trigger 確率追撃 × 6 (event/element 固定、軸 6 draft §2.3 確定値)
//   4. CD 短縮 × 1 (`Skill.cdrRate` で表現、軸 6 archetype 4 専用)
//   5. シールド生成 × 1 (passive 戦闘継続中 1 回適用)
//   6. HP regen × 1 (`Skill.periodTicks` で周期発火)
//   7. 移動速度 × 1
//   8. 命中・回避 × 2 (accuracy / dodge)
//   unique × 5 (juggernaut / runeforge / ironwall / swift-step / versatile-vanilla)
//
// 数値方針 (軸 6 draft §1.2 / §5):
//   - active power: ×1.0 (T1 band 下限寄り、軸 1 element-strike と同帯)
//   - proc strike power: ×0.5 (素 active より弱め、proc trigger の追撃想定)
//   - stat 加算 / shield / HP regen / move-speed / acc-dodge は ItemSkill.values で表現
//   - CD 短縮: `Skill.cdrRate = 0.03` (T1 base)、Tier 上書きは ItemSkill.values
//   - passive Buff の durationTicks は省略 (戦闘継続中無期限、ADR 0014 sim wiring 規約)
//   - passive Shield の durationTicks は seconds(600) (= 戦闘想定上限 10 分、`shield`
//     Effect kind は durationTicks 必須のため値が必要、Phase 4 で校正)
// =============================================================================
const VANILLA_AXIS_6 = {
    // ---------------------------------------------------------------------------
    // archetype 1: 素属性 active × 7 (軸 6 §2.1)
    // ---------------------------------------------------------------------------
    'item.common.vanilla-element-strike.physical': {
        id: 'item.common.vanilla-element-strike.physical',
        name: 'Vanilla Physical Strike',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(3),
        range: 5,
        targeting: { shape: 'singleTarget' },
        activation: {},
        effects: [{ kind: 'damage', element: 'physical', power: 1.0 }],
    },
    'item.common.vanilla-element-strike.fire': {
        id: 'item.common.vanilla-element-strike.fire',
        name: 'Vanilla Fire Strike',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(3),
        range: 20,
        targeting: { shape: 'singleTarget' },
        activation: {},
        effects: [{ kind: 'damage', element: 'fire', power: 1.0 }],
    },
    'item.common.vanilla-element-strike.ice': {
        id: 'item.common.vanilla-element-strike.ice',
        name: 'Vanilla Ice Strike',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(3),
        range: 20,
        targeting: { shape: 'singleTarget' },
        activation: {},
        effects: [{ kind: 'damage', element: 'ice', power: 1.0 }],
    },
    'item.common.vanilla-element-strike.lightning': {
        id: 'item.common.vanilla-element-strike.lightning',
        name: 'Vanilla Lightning Strike',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(3),
        range: 20,
        targeting: { shape: 'singleTarget' },
        activation: {},
        effects: [{ kind: 'damage', element: 'lightning', power: 1.0 }],
    },
    'item.common.vanilla-element-strike.earth': {
        id: 'item.common.vanilla-element-strike.earth',
        name: 'Vanilla Earth Strike',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(3),
        range: 5,
        targeting: { shape: 'singleTarget' },
        activation: {},
        effects: [{ kind: 'damage', element: 'earth', power: 1.0 }],
    },
    'item.common.vanilla-element-strike.light': {
        id: 'item.common.vanilla-element-strike.light',
        name: 'Vanilla Light Strike',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(3),
        range: 12,
        targeting: { shape: 'singleTarget' },
        activation: {},
        effects: [{ kind: 'damage', element: 'light', power: 1.0 }],
    },
    'item.common.vanilla-element-strike.dark': {
        id: 'item.common.vanilla-element-strike.dark',
        name: 'Vanilla Dark Strike',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(3),
        range: 12,
        targeting: { shape: 'singleTarget' },
        activation: {},
        effects: [{ kind: 'damage', element: 'dark', power: 1.0 }],
    },
    // ---------------------------------------------------------------------------
    // archetype 2: 素 passive ステ加算 × 6 (軸 6 §2.1、`stat-boost.<type>` 経由)
    //
    // ItemSkill.values 解釈 (§J #13、テスト fixture で網羅):
    //   - values[tier] = stat 加算 % (例: 0.05 → +5%)
    //   - sim 側 stat 反映 reducer は Phase 4 別 ADR スコープ
    // ---------------------------------------------------------------------------
    'item.common.vanilla-stat-buff.atk-bonus': {
        id: 'item.common.vanilla-stat-buff.atk-bonus',
        name: 'Vanilla ATK Bonus',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    },
    'item.common.vanilla-stat-buff.crit-rate': {
        id: 'item.common.vanilla-stat-buff.crit-rate',
        name: 'Vanilla Crit Rate',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.crit-rate' }],
    },
    'item.common.vanilla-stat-buff.crit-dmg': {
        id: 'item.common.vanilla-stat-buff.crit-dmg',
        name: 'Vanilla Crit Damage',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.crit-dmg' }],
    },
    'item.common.vanilla-stat-buff.armor': {
        id: 'item.common.vanilla-stat-buff.armor',
        name: 'Vanilla Armor',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.armor' }],
    },
    'item.common.vanilla-stat-buff.hp-max': {
        id: 'item.common.vanilla-stat-buff.hp-max',
        name: 'Vanilla HP Max',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.hp-max' }],
    },
    'item.common.vanilla-stat-buff.status-resist': {
        id: 'item.common.vanilla-stat-buff.status-resist',
        name: 'Vanilla Status Resist',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.status-resist' }],
    },
    // ---------------------------------------------------------------------------
    // archetype 3: 素 trigger 確率追撃 × 6 (軸 6 §2.3 確定値、event/element 固定)
    //
    // ItemSkill.values 解釈 (§J #13):
    //   - values[tier] = proc 発動率 (例: 0.05 → 5%)
    //   - proc-chance.ts `readBaseProcChance` で読み出し、80% クランプ後 roll
    // iCD = 30 ticks (§J #6 最小値)、jobTypeBonus は本軸では未設定 (汎用)
    // ---------------------------------------------------------------------------
    'item.common.vanilla-proc-strike.fire-on-hit': {
        id: 'item.common.vanilla-proc-strike.fire-on-hit',
        name: 'Vanilla Fire Proc Strike',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 8,
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onHit' },
        effects: [{ kind: 'damage', element: 'fire', power: 0.5 }],
    },
    'item.common.vanilla-proc-strike.ice-on-hit': {
        id: 'item.common.vanilla-proc-strike.ice-on-hit',
        name: 'Vanilla Ice Proc Strike',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 8,
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onHit' },
        effects: [{ kind: 'damage', element: 'ice', power: 0.5 }],
    },
    'item.common.vanilla-proc-strike.lightning-on-hit': {
        id: 'item.common.vanilla-proc-strike.lightning-on-hit',
        name: 'Vanilla Lightning Proc Strike',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 8,
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onHit' },
        effects: [{ kind: 'damage', element: 'lightning', power: 0.5 }],
    },
    'item.common.vanilla-proc-strike.dark-on-crit': {
        id: 'item.common.vanilla-proc-strike.dark-on-crit',
        name: 'Vanilla Dark Proc Strike',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 8,
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onCrit' },
        effects: [{ kind: 'damage', element: 'dark', power: 0.5 }],
    },
    'item.common.vanilla-proc-strike.light-on-crit': {
        id: 'item.common.vanilla-proc-strike.light-on-crit',
        name: 'Vanilla Light Proc Strike',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 8,
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onCrit' },
        effects: [{ kind: 'damage', element: 'light', power: 0.5 }],
    },
    // onTakenDamage は被害イベント = reactive に分類 (ADR 0013 §2.2.1、kind 選定ルール)
    'item.common.vanilla-proc-strike.earth-on-taken-damage': {
        id: 'item.common.vanilla-proc-strike.earth-on-taken-damage',
        name: 'Vanilla Earth Proc Strike',
        source: 'item',
        kind: 'reactive',
        cooldownTicks: 30,
        range: 8,
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onTakenDamage' },
        effects: [{ kind: 'damage', element: 'earth', power: 0.5 }],
    },
    // ---------------------------------------------------------------------------
    // archetype 4: CD 短縮 × 1 (軸 6 §6.2、`Skill.cdrRate` 経路)
    //
    // ItemSkill.values 解釈 (§J #13):
    //   - values[tier] = CD 短縮率 (例: 0.03 → 3%)、Skill.cdrRate を Tier 上書き
    //   - cd-layers.ts `readCdrRate` で読み出し、axis6 layer に集計、30% クランプ
    // ---------------------------------------------------------------------------
    'item.common.cooldown-mastery': {
        id: 'item.common.cooldown-mastery',
        name: 'Cooldown Mastery',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [],
        cdrRate: 0.03,
    },
    // ---------------------------------------------------------------------------
    // archetype 5: シールド生成 × 1 (軸 6 §6.1、戦闘継続中 1 回適用)
    //
    // ItemSkill.values 解釈 (§J #13):
    //   - values[tier] = max HP × shield ratio (例: 0.10 → max HP 10%)
    // ---------------------------------------------------------------------------
    'item.common.vanilla-shield-generation': {
        id: 'item.common.vanilla-shield-generation',
        name: 'Vanilla Shield Generation',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'shield', power: 0.1, durationTicks: seconds(600) }],
    },
    // ---------------------------------------------------------------------------
    // archetype 6: HP regen × 1 (軸 6 §6 / `Skill.periodTicks` で周期発火)
    //
    // ItemSkill.values 解釈 (§J #13):
    //   - values[tier] = 毎秒 max HP × heal ratio (例: 0.005 → 0.5%/秒)
    // ---------------------------------------------------------------------------
    'item.common.vanilla-hp-regen': {
        id: 'item.common.vanilla-hp-regen',
        name: 'Vanilla HP Regen',
        source: 'item',
        kind: 'passive',
        activation: {},
        periodTicks: seconds(1),
        effects: [{ kind: 'heal', power: 0.005 }],
    },
    // ---------------------------------------------------------------------------
    // archetype 7: 移動速度 × 1 (軸 6 §2.1、`stat-boost.move-speed` 経由)
    // ---------------------------------------------------------------------------
    'item.common.vanilla-move-speed': {
        id: 'item.common.vanilla-move-speed',
        name: 'Vanilla Move Speed',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.move-speed' }],
    },
    // ---------------------------------------------------------------------------
    // archetype 8: 命中・回避 × 2 (軸 6 §2.1、`stat-boost.accuracy` / `stat-boost.dodge` 経由)
    // ---------------------------------------------------------------------------
    'item.common.vanilla-accuracy': {
        id: 'item.common.vanilla-accuracy',
        name: 'Vanilla Accuracy',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.accuracy' }],
    },
    'item.common.vanilla-dodge': {
        id: 'item.common.vanilla-dodge',
        name: 'Vanilla Dodge',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.dodge' }],
    },
    // ---------------------------------------------------------------------------
    // unique × 5 (軸 6 §3)
    //
    // 数値表現は近似 (Phase 4 校正)。draft が想定する trade-off / amp の完全な
    // 機構は sim 側拡張 (別 ADR スコープ) を要するため、本 PR は型レベルで
    // 「flag を立てる passive / trigger」として表現する。
    // ---------------------------------------------------------------------------
    // juggernaut: archetype 1-3 効果量 ×1.5 (近似: atk-bonus passive、Tier 個体差で表現)
    'item.unique.juggernaut.vanilla-amp': {
        id: 'item.unique.juggernaut.vanilla-amp',
        name: 'Juggernaut: Vanilla Amp',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    },
    // runeforge: CD 短縮上限拡張 + 攻撃力 -10% trade-off
    // (`Skill.cdrRate = 0.05` で archetype 4 より高い base、上限拡張は sim 側拡張)
    'item.unique.runeforge.cooldown-mastery': {
        id: 'item.unique.runeforge.cooldown-mastery',
        name: 'Runeforge: Cooldown Mastery',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [],
        cdrRate: 0.05,
    },
    // ironwall: archetype 5 shield 量 2 倍 + HP regen -50% trade-off
    // (power 0.10 → 0.20、trade-off は sim 側拡張)
    'item.unique.ironwall.shield-double': {
        id: 'item.unique.ironwall.shield-double',
        name: 'Ironwall: Shield Double',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'shield', power: 0.2, durationTicks: seconds(600) }],
    },
    // swift-step: onCrit で 3 秒 moveSpeedMod +50%
    'item.unique.swift-step.move-burst': {
        id: 'item.unique.swift-step.move-burst',
        name: 'Swift Step: Move Burst',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: seconds(5),
        activation: { onEvent: 'onCrit' },
        effects: [
            { kind: 'applyBuff', buffId: 'stat-boost.move-speed', durationTicks: seconds(3) },
        ],
    },
    // versatile-vanilla: archetype 3 proc strike 全 trigger 発動率 +10% (近似: atk-bonus、
    // proc 発動率 amp は sim 側拡張)
    'item.unique.versatile-vanilla.proc-amp': {
        id: 'item.unique.versatile-vanilla.proc-amp',
        name: 'Versatile Vanilla: Proc Amp',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    },
};
// =============================================================================
// Step 6.2 軸 9 キル連鎖 (`.context/c5c_killchain_axis.md` v1)
//
// 32 個 = common 25 + unique 7。「敵を倒すたびに何が起きる」 = ハクスラの爽快感ループ。
//
// archetype 別:
//   1. onKill 即時バフ × 3 (atk-bonus / move-speed / crit-rate、5s duration)
//   2. kill-streak counter × 1 (`kill_streak` status を caster に蓄積、max 7 stack)
//   3. kill 追加 active × 3 (damage / heal / buff)
//   4. execute × 1 (敵 HP < 20% で即時 kill 確率、bossFlag/eliteFlag 除外は sim wiring)
//   5. kill 連鎖追撃 × 7 (半径 5m 内別敵に属性ダメ追撃)
//   6. kill spawn × 1 (kill 時に確率で召喚体 1 体 spawn、unitId は軸 4 で確定)
//   7. kill 印付与 × 7 (半径 5m 内全敵に軸 1 mark 1 stack 付与)
//   8. ボス特化 × 1 (`bossFlag` 条件で atkBonus 加算、target.bossFlag 判定は sim wiring)
//   9. 生存連鎖 × 1 (戦闘中無被弾 N 秒で攻撃/機動バフ、`lastTakenDamageTick` counter は sim wiring)
//   unique × 7 (berserker / executioner / bloodlust / massacre / boss-slayer /
//   untouchable / kill-cascade)
//
// 数値方針 (軸 9 §4 / §5):
//   - ItemSkill.values 解釈は test fixture (`TIER_VALUES_FIXTURE`) で網羅
//   - active power: damage 1.0、heal 0.5、spread 0.5、execute 100 (即死級)
//   - cooldownTicks: 30 (proc 系最小値) / 60 (kill spawn / execute) / seconds(5) (unique)
//   - kill 連鎖追撃 半径: 5 (unique で 10 拡張は本 PR では別 unique 値で別 catalogue ID)
//   - status reference: 'kill_streak' (軸 9 既存、ADR 0013 で追加済)、'mark.<element>'
//     (軸 1 既存、ADR 0013 で追加済)、'stat-boost.*' (本 PR Step 0 で追加)
// =============================================================================
const KILLCHAIN_AXIS_9 = {
    // ---------------------------------------------------------------------------
    // archetype 1: onKill 即時バフ × 3 (軸 9 §2.1)
    // ---------------------------------------------------------------------------
    'item.common.instant-kill-bonus.atk-bonus': {
        id: 'item.common.instant-kill-bonus.atk-bonus',
        name: 'Instant Kill Bonus: ATK',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        activation: { onEvent: 'onKill' },
        effects: [
            { kind: 'applyBuff', buffId: 'stat-boost.atk-bonus', durationTicks: seconds(5) },
        ],
    },
    'item.common.instant-kill-bonus.move-speed': {
        id: 'item.common.instant-kill-bonus.move-speed',
        name: 'Instant Kill Bonus: Move Speed',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        activation: { onEvent: 'onKill' },
        effects: [
            { kind: 'applyBuff', buffId: 'stat-boost.move-speed', durationTicks: seconds(5) },
        ],
    },
    'item.common.instant-kill-bonus.crit-rate': {
        id: 'item.common.instant-kill-bonus.crit-rate',
        name: 'Instant Kill Bonus: Crit Rate',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        activation: { onEvent: 'onKill' },
        effects: [
            { kind: 'applyBuff', buffId: 'stat-boost.crit-rate', durationTicks: seconds(5) },
        ],
    },
    // ---------------------------------------------------------------------------
    // archetype 2: kill-streak counter × 1 (軸 9 §6.1)
    //
    // sim 側 reducer (`onKill` で kill_streak stack +1 / `onTakenDamage` で 0 化) は
    // ADR 0014 で実装済 (mark + kill_streak reducer)。catalogue 側は「装備に乗る passive
    // = streak depth で stat 加算が動作する flag」として表現。
    // ---------------------------------------------------------------------------
    'item.common.kill-streak-counter': {
        id: 'item.common.kill-streak-counter',
        name: 'Kill Streak Counter',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'kill_streak' }],
    },
    // ---------------------------------------------------------------------------
    // archetype 3: kill 追加 active × 3 (軸 9 §2.1)
    // ---------------------------------------------------------------------------
    'item.common.kill-cast.damage': {
        id: 'item.common.kill-cast.damage',
        name: 'Kill Cast: Damage',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 60,
        range: 20,
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'damage', element: 'weapon-derived', power: 1.0 }],
    },
    'item.common.kill-cast.heal': {
        id: 'item.common.kill-cast.heal',
        name: 'Kill Cast: Heal',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 60,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'heal', power: 0.5 }],
    },
    'item.common.kill-cast.buff': {
        id: 'item.common.kill-cast.buff',
        name: 'Kill Cast: Buff',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 60,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'applyBuff', buffId: 'mighty', durationTicks: seconds(5) }],
    },
    // ---------------------------------------------------------------------------
    // archetype 4: execute × 1 (軸 9 §6.3)
    //
    // 「敵 HP < 20% に対する攻撃で確率で即時 kill」。即死可否 (bossFlag/eliteFlag 除外)
    // は sim 側 wiring (別 ADR / Phase 4)。catalogue 側は power: 100 で即死級ダメで
    // 表現、unique で閾値 / 確率を拡張。
    // ---------------------------------------------------------------------------
    'item.common.execute': {
        id: 'item.common.execute',
        name: 'Execute',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 60,
        range: 8,
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onHit', targetHpAtMost: 0.2 },
        effects: [{ kind: 'damage', element: 'weapon-derived', power: 100 }],
    },
    // ---------------------------------------------------------------------------
    // archetype 5: kill 連鎖追撃 × 7 (軸 9 §2.1、属性別 7 個)
    //
    // kill 時に半径 5m 内別敵に当該属性ダメ追撃。半径は ItemSkill.values で表現せず
    // catalogue 側 `targeting.radius` で固定 (unique `kill-cascade.chain-radius` で
    // 10m 拡張用の別 catalogue ID は本 PR では作らず unique 強化のみ)。
    // ---------------------------------------------------------------------------
    'item.common.kill-spread.physical': {
        id: 'item.common.kill-spread.physical',
        name: 'Kill Spread: Physical',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'damage', element: 'physical', power: 0.5 }],
    },
    'item.common.kill-spread.fire': {
        id: 'item.common.kill-spread.fire',
        name: 'Kill Spread: Fire',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'damage', element: 'fire', power: 0.5 }],
    },
    'item.common.kill-spread.ice': {
        id: 'item.common.kill-spread.ice',
        name: 'Kill Spread: Ice',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'damage', element: 'ice', power: 0.5 }],
    },
    'item.common.kill-spread.lightning': {
        id: 'item.common.kill-spread.lightning',
        name: 'Kill Spread: Lightning',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'damage', element: 'lightning', power: 0.5 }],
    },
    'item.common.kill-spread.earth': {
        id: 'item.common.kill-spread.earth',
        name: 'Kill Spread: Earth',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'damage', element: 'earth', power: 0.5 }],
    },
    'item.common.kill-spread.light': {
        id: 'item.common.kill-spread.light',
        name: 'Kill Spread: Light',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'damage', element: 'light', power: 0.5 }],
    },
    'item.common.kill-spread.dark': {
        id: 'item.common.kill-spread.dark',
        name: 'Kill Spread: Dark',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'damage', element: 'dark', power: 0.5 }],
    },
    // ---------------------------------------------------------------------------
    // archetype 6: kill spawn × 1 (軸 9 §2.1 / §G1)
    //
    // kill 時に確率で召喚体 spawn (軸 4 召喚体上限 20 体に従う、上限到達時は発動失敗)。
    // unitId は軸 4 catalogue で確定する想定、本 PR では plateholder。
    // ---------------------------------------------------------------------------
    'item.common.kill-spawn': {
        id: 'item.common.kill-spawn',
        name: 'Kill Spawn',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 60,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onKill' },
        effects: [
            {
                kind: 'summon',
                spec: { unitId: 'kill-spawn.minion', durationTicks: seconds(45), count: 1 },
            },
        ],
    },
    // ---------------------------------------------------------------------------
    // archetype 7: kill 印付与 × 7 (軸 9 §2.1、属性別 7 個)
    // ---------------------------------------------------------------------------
    'item.common.kill-mark.physical': {
        id: 'item.common.kill-mark.physical',
        name: 'Kill Mark: Physical',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'applyDebuff', debuffId: 'mark.physical', stacks: 1 }],
    },
    'item.common.kill-mark.fire': {
        id: 'item.common.kill-mark.fire',
        name: 'Kill Mark: Fire',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'applyDebuff', debuffId: 'mark.fire', stacks: 1 }],
    },
    'item.common.kill-mark.ice': {
        id: 'item.common.kill-mark.ice',
        name: 'Kill Mark: Ice',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'applyDebuff', debuffId: 'mark.ice', stacks: 1 }],
    },
    'item.common.kill-mark.lightning': {
        id: 'item.common.kill-mark.lightning',
        name: 'Kill Mark: Lightning',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'applyDebuff', debuffId: 'mark.lightning', stacks: 1 }],
    },
    'item.common.kill-mark.earth': {
        id: 'item.common.kill-mark.earth',
        name: 'Kill Mark: Earth',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'applyDebuff', debuffId: 'mark.earth', stacks: 1 }],
    },
    'item.common.kill-mark.light': {
        id: 'item.common.kill-mark.light',
        name: 'Kill Mark: Light',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'applyDebuff', debuffId: 'mark.light', stacks: 1 }],
    },
    'item.common.kill-mark.dark': {
        id: 'item.common.kill-mark.dark',
        name: 'Kill Mark: Dark',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'applyDebuff', debuffId: 'mark.dark', stacks: 1 }],
    },
    // ---------------------------------------------------------------------------
    // archetype 8: ボス特化 × 1 (軸 9 §6.3)
    //
    // bossFlag 持ち敵への atkBonus 加算。target.bossFlag 判定は sim 側 wiring
    // (種族名ベース判定は不採用、Q-SS ISSUE 高対応)。catalogue 側は flag 立てるパッシブ。
    // ---------------------------------------------------------------------------
    'item.common.boss-hunter': {
        id: 'item.common.boss-hunter',
        name: 'Boss Hunter',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    },
    // ---------------------------------------------------------------------------
    // archetype 9: 生存連鎖 × 1 (軸 9 §6.2)
    //
    // 戦闘中無被弾 N 秒で攻撃/機動バフ発動。新規 status 追加せず、sim 内部の
    // caster `lastTakenDamageTick` counter で判定 (Q-SS SUGG 段階導入、軸 6 archetype 6
    // との重複回避)。catalogue 側は「攻撃/機動バフ」flag を立てるパッシブ。
    // ---------------------------------------------------------------------------
    'item.common.no-death-bonus': {
        id: 'item.common.no-death-bonus',
        name: 'No-Death Bonus',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    },
    // ---------------------------------------------------------------------------
    // unique × 7 (軸 9 §3)
    // ---------------------------------------------------------------------------
    // berserker: streak depth で全 atkBonus +X%/段階 (近似: streak counter 装備で
    // atkBonus を上乗せ、Tier 個体差)
    'item.unique.berserker.kill-frenzy': {
        id: 'item.unique.berserker.kill-frenzy',
        name: 'Berserker: Kill Frenzy',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    },
    // executioner: execute 閾値 20% → 35% に拡張 (近似: 閾値 0.35 で別 trigger)
    'item.unique.executioner.true-finisher': {
        id: 'item.unique.executioner.true-finisher',
        name: 'Executioner: True Finisher',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 60,
        range: 8,
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onHit', targetHpAtMost: 0.35 },
        effects: [{ kind: 'damage', element: 'weapon-derived', power: 100 }],
    },
    // bloodlust: kill 時の heal 量を 2 倍 (archetype 3 heal 派生強化、近似: heal power 2 倍)
    'item.unique.bloodlust.heal-on-kill-amp': {
        id: 'item.unique.bloodlust.heal-on-kill-amp',
        name: 'Bloodlust: Heal-on-Kill Amp',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 60,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'heal', power: 1.0 }],
    },
    // massacre: streak 維持時間 2 倍、被弾でも 1 段階のみ消費 (近似: 上書き passive、
    // 実 trade-off は sim 拡張)
    'item.unique.massacre.streak-extend': {
        id: 'item.unique.massacre.streak-extend',
        name: 'Massacre: Streak Extend',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'kill_streak' }],
    },
    // boss-slayer: bossFlag 持ち敵への atkBonus +X% (Tier で +30〜+70%)
    'item.unique.boss-slayer.giant-killer': {
        id: 'item.unique.boss-slayer.giant-killer',
        name: 'Boss Slayer: Giant Killer',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    },
    // untouchable: 生存連鎖バフ強度 ×2 (近似: atkBonus passive 重ね、実数値は sim 拡張)
    'item.unique.untouchable.no-death-amp': {
        id: 'item.unique.untouchable.no-death-amp',
        name: 'Untouchable: No-Death Amp',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    },
    // kill-cascade: archetype 5 半径 5m → 10m に拡張 (近似: radius 10 の別 trigger を passive 化、
    // catalogue 側は radius を含む trigger として書き、装備時に既存 archetype 5 と重ねがけ可)
    'item.unique.kill-cascade.chain-radius': {
        id: 'item.unique.kill-cascade.chain-radius',
        name: 'Kill Cascade: Chain Radius',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 10,
        targeting: { shape: 'circle', radius: 10, maxTargets: 15 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'damage', element: 'physical', power: 0.5 }],
    },
};
// =============================================================================
// Step 6.3 軸 1 属性スタッキング (`.context/c5c_attribute_axis.md` v2)
//
// 75 個 = common 56 (7 属性 × 8 archetype) + unique 19 (D.1 14 + D.2 5)。
// 「印 (Elemental Mark) を中心とした 7 属性ビルド」を catalogue 化。
//
// 8 archetype × 7 属性:
//   1. 印付与 (Mark Apply) trigger — onHit で `mark.<element>` 1 stack 付与
//      (ADR 0013 §2.2.1 kind 定義に従い trigger、draft v2 §C.3 表記の `passive` は
//      pre-ADR-0013 表記、本 PR では trigger に統一)
//   2. 印消費バースト (Mark Burst) active — `targetHasStatusAtLeast` ゲート +
//      `scalesWithStatusStacks` 動的乗算 + `removeStatusById` 全消費
//   3. 同属性ブースト (Same-Element Boost) passive — target の印数で active power
//      乗算 (sim 拡張、近似 stat-boost.atk-bonus)
//   4. 純素属性 active (Element Strike) active — 各属性 base active
//   5. 属性トリガー (Element Trigger) trigger — onCrit/onHit で確率追撃
//   6. sub-tag 表現 (Resist Shred) passive — 属性別 sub-tag (§B、近似 stat-boost)
//   7. 印維持エンジン (Mark Sustain) passive — `decayTicks` 延長 (sim 拡張、近似 flag)
//   8. 消費後リカバリ (Burst Refund) trigger — onMarkBurst で印 1 stack 返還 (近似)
//
// unique 14 + 新 8 kind 借用 5 = 19 個 (軸 1 §D.1 / §D.2)
// =============================================================================
const ATTRIBUTE_AXIS_1 = (() => {
    const out = {};
    for (const element of ELEMENTS_7) {
        // archetype 1: 印付与 trigger (onHit で mark.<element> 1 stack)
        out[`item.common.elemental-mark.${element}`] = {
            id: `item.common.elemental-mark.${element}`,
            name: `Elemental Mark: ${element}`,
            source: 'item',
            kind: 'trigger',
            cooldownTicks: 30,
            targeting: { shape: 'singleTarget' },
            activation: { onEvent: 'onHit' },
            effects: [{ kind: 'applyDebuff', debuffId: markId(element), stacks: 1 }],
        };
        // archetype 2: 印消費バースト active
        out[`item.common.mark-burst.${element}`] = {
            id: `item.common.mark-burst.${element}`,
            name: `Mark Burst: ${element}`,
            source: 'item',
            kind: 'active',
            cooldownTicks: seconds(30),
            range: 6,
            targeting: { shape: 'singleTarget' },
            activation: {
                targetHasStatusAtLeast: { statusId: markId(element), stacks: 1 },
            },
            effects: [
                {
                    kind: 'damage',
                    element: element,
                    power: 1.0,
                    scalesWithStatusStacks: { statusId: markId(element), perStackMod: 1.0 },
                },
                { kind: 'removeStatusById', statusId: markId(element), stacks: 'all' },
            ],
        };
        // archetype 3: 同属性ブースト passive (target.mark 参照は sim 拡張、近似 stat-boost.atk-bonus)
        out[`item.common.same-element-boost.${element}`] = {
            id: `item.common.same-element-boost.${element}`,
            name: `Same-Element Boost: ${element}`,
            source: 'item',
            kind: 'passive',
            activation: {},
            effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
        };
        // archetype 4: 純素属性 active (base active per element)
        out[`item.common.element-strike.${element}`] = {
            id: `item.common.element-strike.${element}`,
            name: `Element Strike: ${element}`,
            source: 'item',
            kind: 'active',
            cooldownTicks: seconds(3),
            range: element === 'physical' || element === 'earth' ? 5 : 20,
            targeting: { shape: 'singleTarget' },
            activation: {},
            effects: [{ kind: 'damage', element: element, power: 1.0 }],
        };
        // archetype 5: 属性トリガー trigger (onCrit/onHit で確率追撃)
        out[`item.common.element-trigger.${element}`] = {
            id: `item.common.element-trigger.${element}`,
            name: `Element Trigger: ${element}`,
            source: 'item',
            kind: 'trigger',
            cooldownTicks: 30,
            targeting: { shape: 'singleTarget' },
            activation: { onEvent: 'onCrit' },
            effects: [{ kind: 'damage', element: element, power: 0.5 }],
        };
        // archetype 6: sub-tag 表現 (Resist Shred) passive
        // 属性別 sub-tag (§B): physical=armor 貫通 / fire=耐性減衰 / ice=曝露 等
        // catalogue 内では `resist-shred.<element>` で統一命名、機能差は sim 拡張
        out[`item.common.resist-shred.${element}`] = {
            id: `item.common.resist-shred.${element}`,
            name: `Resist Shred: ${element}`,
            source: 'item',
            kind: 'passive',
            activation: {},
            effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
        };
        // archetype 7: 印維持エンジン (Mark Sustain) passive — decayTicks 延長は sim 拡張
        out[`item.common.mark-sustain.${element}`] = {
            id: `item.common.mark-sustain.${element}`,
            name: `Mark Sustain: ${element}`,
            source: 'item',
            kind: 'passive',
            activation: {},
            effects: [{ kind: 'applyBuff', buffId: markId(element) }],
        };
        // archetype 8: 消費後リカバリ (Burst Refund) trigger — onMarkBurst
        // archetype 2 (mark-burst) 発動直後に確率で印を 1 stack 返還 (近似: applyDebuff stack)
        out[`item.common.burst-refund.${element}`] = {
            id: `item.common.burst-refund.${element}`,
            name: `Burst Refund: ${element}`,
            source: 'item',
            kind: 'trigger',
            cooldownTicks: 30,
            targeting: { shape: 'singleTarget' },
            activation: { onEvent: 'onMarkBurst' },
            effects: [{ kind: 'applyDebuff', debuffId: markId(element), stacks: 1 }],
        };
    }
    // ---------------------------------------------------------------------------
    // unique × 19 (軸 1 §D)
    //
    // §D.1 通常 unique 14 個 (属性別 2 個ずつ) + §D.2 新 8 kind 借用 5 個。
    // 各 unique は近似実装で、完全な機構は sim 拡張 (Phase 4)。
    // ---------------------------------------------------------------------------
    // physical 2
    out['item.unique.crimson-thirst.bloodlust'] = {
        id: 'item.unique.crimson-thirst.bloodlust',
        name: 'Crimson Thirst: Bloodlust',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: seconds(5),
        targeting: { shape: 'self' },
        activation: {
            onEvent: 'onKill',
            targetHasStatusAtLeast: { statusId: markId('physical'), stacks: 3 },
        },
        effects: [
            { kind: 'applyBuff', buffId: 'stat-boost.atk-bonus', durationTicks: seconds(8) },
        ],
    };
    out['item.unique.ironbreaker.armor-shatter'] = {
        id: 'item.unique.ironbreaker.armor-shatter',
        name: 'Ironbreaker: Armor Shatter',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onCrit' },
        effects: [
            {
                kind: 'applyDebuff',
                debuffId: 'armor_break',
                stacks: 1,
                chanceScalesWithStatusStacks: { statusId: markId('physical'), perStackMod: 0.2 },
            },
        ],
    };
    // ice 2
    out['item.unique.frost-shroud.shatter'] = {
        id: 'item.unique.frost-shroud.shatter',
        name: 'Frost Shroud: Shatter',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: seconds(2),
        targeting: { shape: 'singleTarget' },
        activation: {
            onEvent: 'onHit',
            targetHasStatusAtLeast: { statusId: markId('ice'), stacks: 3 },
        },
        effects: [{ kind: 'applyDebuff', debuffId: 'freeze', stacks: 1 }],
    };
    out['item.unique.frozen-tear.refresh'] = {
        id: 'item.unique.frozen-tear.refresh',
        name: 'Frozen Tear: Refresh',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: markId('ice') }],
    };
    // lightning 2
    out['item.unique.thunder-mantle.chain-arc'] = {
        id: 'item.unique.thunder-mantle.chain-arc',
        name: 'Thunder Mantle: Chain Arc',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(8),
        range: 12,
        targeting: { shape: 'singleTarget', maxTargets: 5 },
        activation: {},
        effects: [{ kind: 'damage', element: 'lightning', power: 1.2 }],
    };
    out['item.unique.storm-rider.haste'] = {
        id: 'item.unique.storm-rider.haste',
        name: 'Storm Rider: Haste',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.move-speed' }],
    };
    // fire 2
    out['item.unique.flame-veil.bloom'] = {
        id: 'item.unique.flame-veil.bloom',
        name: 'Flame Veil: Bloom',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(15),
        range: 8,
        targeting: { shape: 'circle', radius: 5, maxTargets: 8 },
        activation: {
            targetHasStatusAtLeast: { statusId: markId('fire'), stacks: 1 },
        },
        effects: [
            { kind: 'damage', element: 'fire', power: 1.5 },
            { kind: 'applyDebuff', debuffId: 'burn', stacks: 1 },
            { kind: 'removeStatusById', statusId: markId('fire'), stacks: 'all' },
        ],
    };
    out['item.unique.ember-heart.recovery'] = {
        id: 'item.unique.ember-heart.recovery',
        name: 'Ember Heart: Recovery',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onMarkBurst' },
        effects: [{ kind: 'heal', power: 0.1 }],
    };
    // earth 2 (設置物軸 3 との交差)
    out['item.unique.earth-wall.placeable-link'] = {
        id: 'item.unique.earth-wall.placeable-link',
        name: 'Earth Wall: Placeable Link',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(15),
        range: 5,
        targeting: { shape: 'self' },
        activation: {
            targetHasStatusAtLeast: { statusId: markId('earth'), stacks: 1 },
        },
        effects: [
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'earth-wall',
                    radius: 1,
                    durationTicks: seconds(20),
                    maxConcurrentPerOwner: 3,
                    isWalkable: false,
                    hp: 500,
                    trigger: { kind: 'aura-zone' },
                    onTriggerEffects: [],
                },
            },
            { kind: 'removeStatusById', statusId: markId('earth'), stacks: 'all' },
        ],
    };
    out['item.unique.stone-pact.heal-on-place'] = {
        id: 'item.unique.stone-pact.heal-on-place',
        name: 'Stone Pact: Heal on Place',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onPlaceableTrigger' },
        effects: [{ kind: 'heal', power: 0.2 }],
    };
    // light 2
    out['item.unique.light-covenant.purify-burst'] = {
        id: 'item.unique.light-covenant.purify-burst',
        name: 'Light Covenant: Purify Burst',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(20),
        targeting: { shape: 'allAllies' },
        activation: {
            targetHasStatusAtLeast: { statusId: markId('light'), stacks: 1 },
        },
        effects: [
            { kind: 'cleanse', count: 3 },
            { kind: 'applyBuff', buffId: 'blessing', durationTicks: seconds(10) },
            { kind: 'removeStatusById', statusId: markId('light'), stacks: 'all' },
        ],
    };
    out['item.unique.dawn-beacon.guardian'] = {
        id: 'item.unique.dawn-beacon.guardian',
        name: 'Dawn Beacon: Guardian',
        source: 'item',
        kind: 'reactive',
        cooldownTicks: seconds(10),
        targeting: { shape: 'allyLowestHp' },
        activation: { onEvent: 'onAllyHpBelow' },
        effects: [
            { kind: 'applyDebuff', debuffId: markId('light'), stacks: 1 },
            { kind: 'shield', power: 1.0, durationTicks: seconds(8) },
        ],
    };
    // dark 2
    out['item.unique.shadow-pact.dot-cascade'] = {
        id: 'item.unique.shadow-pact.dot-cascade',
        name: 'Shadow Pact: DoT Cascade',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onHit' },
        effects: [
            {
                kind: 'applyDebuff',
                debuffId: 'poison',
                stacks: 1,
                chanceScalesWithStatusStacks: { statusId: markId('dark'), perStackMod: 0.2 },
            },
        ],
    };
    out['item.unique.void-bargain.sacrifice'] = {
        id: 'item.unique.void-bargain.sacrifice',
        name: 'Void Bargain: Sacrifice',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(30),
        targeting: { shape: 'self' },
        activation: {},
        hpCost: 100,
        effects: [
            { kind: 'applyBuff', buffId: markId('dark'), stacks: 1, durationTicks: seconds(8) },
        ],
    };
    // §D.2 新 8 kind 借用 unique 5
    out['item.unique.flame-conduit.element-conversion'] = {
        id: 'item.unique.flame-conduit.element-conversion',
        name: 'Flame Conduit: Element Conversion',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [
            {
                kind: 'damageConversion',
                fraction: 0.3,
                durationTicks: seconds(600),
                payoutShape: 'self',
            },
        ],
    };
    out['item.unique.frost-bulwark.reflect-projectile'] = {
        id: 'item.unique.frost-bulwark.reflect-projectile',
        name: 'Frost Bulwark: Reflect Projectile',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [
            {
                kind: 'reflectingProjectile',
                projectileCount: 1,
                maxBounces: 2,
                onBounceBuff: { buffId: 'composure', stacks: 1, durationTicks: seconds(4) },
            },
        ],
    };
    out['item.unique.iaido-spirit.counter-stance'] = {
        id: 'item.unique.iaido-spirit.counter-stance',
        name: 'Iaido Spirit: Counter Stance',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(15),
        targeting: { shape: 'self' },
        activation: {},
        effects: [
            {
                kind: 'counterStance',
                durationTicks: seconds(5),
                counterPower: 0.45,
                counterElement: 'physical',
            },
        ],
    };
    out['item.unique.gathering-storm.damage-store'] = {
        id: 'item.unique.gathering-storm.damage-store',
        name: 'Gathering Storm: Damage Store',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(20),
        targeting: { shape: 'self' },
        activation: {},
        effects: [
            {
                kind: 'damageStoreAndRelease',
                storeDurationTicks: 120,
                incomingDamageReduction: 0.3,
                releaseMultiplier: 1.0,
                releaseShape: 'circle',
                releaseRadius: 8,
                releaseElement: 'lightning',
            },
        ],
    };
    out['item.unique.light-bastion.shared-absorb'] = {
        id: 'item.unique.light-bastion.shared-absorb',
        name: 'Light Bastion: Shared Absorb',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(25),
        targeting: { shape: 'allAllies' },
        activation: {},
        effects: [
            {
                kind: 'sharedAbsorbCharge',
                durationTicks: seconds(8),
                totalAbsorbRatio: 0.2,
                overflowToHealRatio: 0.3,
                areaRadius: 8,
            },
        ],
    };
    return out;
})();
const AXIS_2_STATUS_BY_ELEMENT = {
    physical: {
        stackingDebuff: 'bleed',
        severeForm: 'crush',
        stackingBuff: 'fighting_spirit',
        upperForm: 'mighty',
        singleBuff: 'stance',
        singleDebuff: 'armor_break',
    },
    fire: {
        stackingDebuff: 'burn',
        severeForm: 'scald',
        stackingBuff: 'heat_aura',
        upperForm: 'fire_circle',
        singleBuff: 'ignite',
        singleDebuff: 'parch',
    },
    ice: {
        stackingDebuff: 'chill',
        severeForm: 'freeze',
        stackingBuff: 'ice_film',
        upperForm: 'ice_wall',
        singleBuff: 'composure',
        singleDebuff: 'frostbind',
    },
    lightning: {
        stackingDebuff: 'shock',
        severeForm: 'paralyze',
        stackingBuff: 'charge',
        upperForm: 'overcharge',
        singleBuff: 'flash',
        singleDebuff: 'overload',
    },
    earth: {
        stackingDebuff: 'pressure',
        severeForm: 'petrify',
        stackingBuff: 'harden',
        upperForm: 'earth_guard',
        singleBuff: 'shelter',
        singleDebuff: 'brittle',
    },
    light: {
        stackingDebuff: 'dazzle',
        severeForm: 'stigma',
        stackingBuff: 'blessing',
        upperForm: 'divine_grace',
        singleBuff: 'cleanse',
        singleDebuff: 'seal',
    },
    dark: {
        stackingDebuff: 'poison',
        severeForm: 'erosion',
        stackingBuff: 'absorb',
        upperForm: 'blood_oath',
        singleBuff: 'shadow_veil',
        singleDebuff: 'curse',
    },
};
const STATUS_AXIS_2 = (() => {
    const out = {};
    for (const element of ELEMENTS_7) {
        const m = AXIS_2_STATUS_BY_ELEMENT[element];
        // archetype 1: debuff-amp passive (onHit で stacking-debuff 1 stack 付与)
        out[`item.common.debuff-amp.${element}`] = {
            id: `item.common.debuff-amp.${element}`,
            name: `Debuff Amp: ${element}`,
            source: 'item',
            kind: 'trigger',
            cooldownTicks: 30,
            targeting: { shape: 'singleTarget' },
            activation: { onEvent: 'onHit' },
            effects: [{ kind: 'applyDebuff', debuffId: m.stackingDebuff, stacks: 1 }],
        };
        // archetype 2: debuff-burst active (印消費型 mark-burst と同形)
        out[`item.common.debuff-burst.${element}`] = {
            id: `item.common.debuff-burst.${element}`,
            name: `Debuff Burst: ${element}`,
            source: 'item',
            kind: 'active',
            cooldownTicks: seconds(20),
            range: 8,
            targeting: { shape: 'singleTarget' },
            activation: {
                targetHasStatusAtLeast: { statusId: m.stackingDebuff, stacks: 1 },
            },
            effects: [
                {
                    kind: 'damage',
                    element: element,
                    power: 1.0,
                    scalesWithStatusStacks: { statusId: m.stackingDebuff, perStackMod: 1.0 },
                },
                { kind: 'removeStatusById', statusId: m.stackingDebuff, stacks: 'all' },
            ],
        };
        // archetype 3: severe-strike trigger (onStatusSevere で重症形遷移時に追撃)
        out[`item.common.severe-strike.${element}`] = {
            id: `item.common.severe-strike.${element}`,
            name: `Severe Strike: ${element}`,
            source: 'item',
            kind: 'trigger',
            cooldownTicks: 30,
            targeting: { shape: 'singleTarget' },
            activation: {
                onEvent: 'onStatusSevere',
                targetHasStatusAtLeast: { statusId: m.severeForm, stacks: 1 },
            },
            effects: [{ kind: 'damage', element: element, power: 1.0 }],
        };
        // archetype 4: buff-amp passive (stacking-buff 付与)
        out[`item.common.buff-amp.${element}`] = {
            id: `item.common.buff-amp.${element}`,
            name: `Buff Amp: ${element}`,
            source: 'item',
            kind: 'passive',
            activation: {},
            effects: [{ kind: 'applyBuff', buffId: m.stackingBuff, stacks: 1 }],
        };
        // archetype 5: upper-trigger (onStatusUpgrade で上位形遷移時に強化バフ発動)
        out[`item.common.upper-trigger.${element}`] = {
            id: `item.common.upper-trigger.${element}`,
            name: `Upper Trigger: ${element}`,
            source: 'item',
            kind: 'trigger',
            cooldownTicks: 30,
            targeting: { shape: 'self' },
            activation: { onEvent: 'onStatusUpgrade' },
            effects: [{ kind: 'applyBuff', buffId: m.upperForm, durationTicks: seconds(8) }],
        };
        // archetype 6: charge-buff active (単発バフ追加付与)
        out[`item.common.charge-buff.${element}`] = {
            id: `item.common.charge-buff.${element}`,
            name: `Charge Buff: ${element}`,
            source: 'item',
            kind: 'active',
            cooldownTicks: seconds(15),
            targeting: { shape: 'self' },
            activation: {},
            effects: [{ kind: 'applyBuff', buffId: m.singleBuff, durationTicks: seconds(6) }],
        };
        // archetype 7: debuff-sustain passive (持続延長は sim 拡張、近似 stat-boost flag)
        out[`item.common.debuff-sustain.${element}`] = {
            id: `item.common.debuff-sustain.${element}`,
            name: `Debuff Sustain: ${element}`,
            source: 'item',
            kind: 'passive',
            activation: {},
            effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
        };
        // archetype 8: mark-debuff-link trigger (onHit + chanceScalesWithStatusStacks)
        out[`item.common.mark-debuff-link.${element}`] = {
            id: `item.common.mark-debuff-link.${element}`,
            name: `Mark-Debuff Link: ${element}`,
            source: 'item',
            kind: 'trigger',
            cooldownTicks: 30,
            targeting: { shape: 'singleTarget' },
            activation: { onEvent: 'onHit' },
            effects: [
                {
                    kind: 'applyDebuff',
                    debuffId: m.stackingDebuff,
                    stacks: 1,
                    chanceScalesWithStatusStacks: { statusId: markId(element), perStackMod: 0.05 },
                },
            ],
        };
        // archetype 9: stack-bonus passive (維持型、sim 拡張で stack 数で乗算)
        out[`item.common.stack-bonus.${element}`] = {
            id: `item.common.stack-bonus.${element}`,
            name: `Stack Bonus: ${element}`,
            source: 'item',
            kind: 'passive',
            activation: {},
            effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
        };
    }
    // archetype 10: status-cleanse active (共通 1 個、属性別ではない)
    out['item.common.status-cleanse'] = {
        id: 'item.common.status-cleanse',
        name: 'Status Cleanse',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(15),
        targeting: { shape: 'allyLowestHp' },
        activation: {},
        effects: [{ kind: 'cleanse', count: 2 }],
    };
    // ---------------------------------------------------------------------------
    // unique × 10 (軸 2 §D)
    // ---------------------------------------------------------------------------
    out['item.unique.crimson-tide.bleed-amp'] = {
        id: 'item.unique.crimson-tide.bleed-amp',
        name: 'Crimson Tide: Bleed Amp',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.unique.severe-judgment.execute'] = {
        id: 'item.unique.severe-judgment.execute',
        name: 'Severe Judgment: Execute',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(15),
        range: 8,
        targeting: { shape: 'singleTarget' },
        activation: {
            targetHasStatusAtLeast: { statusId: 'crush', stacks: 1 },
        },
        effects: [{ kind: 'damage', element: 'physical', power: 1.5 }],
    };
    out['item.unique.ember-ignite.double-charge'] = {
        id: 'item.unique.ember-ignite.double-charge',
        name: 'Ember Ignite: Double Charge',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'ignite' }],
    };
    out['item.unique.frost-stillness.freeze-amp'] = {
        id: 'item.unique.frost-stillness.freeze-amp',
        name: 'Frost Stillness: Freeze Amp',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: seconds(5),
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 8 },
        activation: { onEvent: 'onStatusSevere' },
        effects: [{ kind: 'applyDebuff', debuffId: 'chill', stacks: 1 }],
    };
    out['item.unique.thundercall.paralyze-chain'] = {
        id: 'item.unique.thundercall.paralyze-chain',
        name: 'Thundercall: Paralyze Chain',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(12),
        range: 8,
        targeting: { shape: 'circle', radius: 5, maxTargets: 5 },
        activation: {
            targetHasStatusAtLeast: { statusId: 'paralyze', stacks: 1 },
        },
        effects: [
            { kind: 'damage', element: 'lightning', power: 0.5 },
            { kind: 'applyDebuff', debuffId: 'paralyze', stacks: 1 },
        ],
    };
    out['item.unique.earth-trial.petrify-shatter'] = {
        id: 'item.unique.earth-trial.petrify-shatter',
        name: 'Earth Trial: Petrify Shatter',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: seconds(3),
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onStatusSevere' },
        effects: [{ kind: 'applyDebuff', debuffId: 'armor_break', stacks: 1 }],
    };
    out['item.unique.holy-judgment.stigma-erupt'] = {
        id: 'item.unique.holy-judgment.stigma-erupt',
        name: 'Holy Judgment: Stigma Erupt',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: seconds(2),
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onStatusSevere' },
        effects: [{ kind: 'damage', element: 'light', power: 1.0 }],
    };
    out['item.unique.poison-pact.dot-stack-cap'] = {
        id: 'item.unique.poison-pact.dot-stack-cap',
        name: 'Poison Pact: DoT Stack Cap',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.unique.blessing-resonance.upgrade-share'] = {
        id: 'item.unique.blessing-resonance.upgrade-share',
        name: 'Blessing Resonance: Upgrade Share',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        targeting: { shape: 'allAllies' },
        activation: { onEvent: 'onStatusUpgrade' },
        effects: [{ kind: 'applyBuff', buffId: 'fighting_spirit', stacks: 1 }],
    };
    out['item.unique.curse-cascade.dot-spread'] = {
        id: 'item.unique.curse-cascade.dot-spread',
        name: 'Curse Cascade: DoT Spread',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'applyDebuff', debuffId: 'poison', stacks: 1 }],
    };
    return out;
})();
// =============================================================================
// Step 6.5 軸 7 資源循環 (`.context/c5c_resource_axis.md` v1)
//
// 26 個 = common 19 + unique 7。MP 廃止済本作の「リソース循環ループ」 = CD/HP/Stack
// を消費・生成・変換する装備スキル群。Charge メカは G1-A で不採用 (本軸新型ゼロ)。
//
// archetype 別 (§2.1):
//   1. CD リセット × 3 (onKill / onCrit / onTakenDamage、近似: applyBuff flag、
//      sim 拡張で CD 即時 0 化)
//   2. CD 短縮 chain × 1 (active 発動後 N 秒間別 active CD -X%、sim 拡張、近似 flag)
//   3. HP コスト発動 × 3 (offensive / defensive / utility、`Skill.hpCost` 使用)
//   4. Resource Conversion × 2 (HP↔Shield、direction パラメータで双方向)
//   5. Stack Overflow Conversion × 7 (属性別、onMarkBurst + 属性別 effect、
//      stack 全消費は軸 1 mark-burst 経路で完了済、本軸は overflow 効果のみ)
//   6. 回復循環 × 3 (heal-on-hit / heal-on-taken-damage / heal-on-kill)
//
// unique 7 個: unstoppable / berserker / vampiric-blade / crystal-armor /
// overflow-master / eternal-cycle / death-pact
// =============================================================================
const RESOURCE_AXIS_7 = (() => {
    const out = {};
    // ---------------------------------------------------------------------------
    // archetype 1: CD リセット × 3 (近似 flag、sim 拡張)
    // ---------------------------------------------------------------------------
    out['item.common.cooldown-reset.on-kill'] = {
        id: 'item.common.cooldown-reset.on-kill',
        name: 'Cooldown Reset: on Kill',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 60,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus', durationTicks: 1 }],
    };
    out['item.common.cooldown-reset.on-crit'] = {
        id: 'item.common.cooldown-reset.on-crit',
        name: 'Cooldown Reset: on Crit',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 60,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onCrit' },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus', durationTicks: 1 }],
    };
    out['item.common.cooldown-reset.on-taken-damage'] = {
        id: 'item.common.cooldown-reset.on-taken-damage',
        name: 'Cooldown Reset: on Taken Damage',
        source: 'item',
        kind: 'reactive',
        cooldownTicks: 60,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onTakenDamage' },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus', durationTicks: 1 }],
    };
    // ---------------------------------------------------------------------------
    // archetype 2: CD 短縮 chain × 1 (近似 flag、sim 拡張で active 発動後 N 秒 CD -X%)
    // ---------------------------------------------------------------------------
    out['item.common.cooldown-chain'] = {
        id: 'item.common.cooldown-chain',
        name: 'Cooldown Chain',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    // ---------------------------------------------------------------------------
    // archetype 3: HP コスト発動 × 3 (offensive / defensive / utility)
    // ---------------------------------------------------------------------------
    out['item.common.hp-sacrifice-cast.offensive'] = {
        id: 'item.common.hp-sacrifice-cast.offensive',
        name: 'HP Sacrifice Cast: Offensive',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(8),
        range: 12,
        targeting: { shape: 'singleTarget' },
        activation: {},
        hpCost: 50,
        effects: [{ kind: 'damage', element: 'weapon-derived', power: 1.2 }],
    };
    out['item.common.hp-sacrifice-cast.defensive'] = {
        id: 'item.common.hp-sacrifice-cast.defensive',
        name: 'HP Sacrifice Cast: Defensive',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(10),
        targeting: { shape: 'self' },
        activation: {},
        hpCost: 50,
        effects: [{ kind: 'shield', power: 0.3, durationTicks: seconds(8) }],
    };
    out['item.common.hp-sacrifice-cast.utility'] = {
        id: 'item.common.hp-sacrifice-cast.utility',
        name: 'HP Sacrifice Cast: Utility',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(10),
        targeting: { shape: 'self' },
        activation: {},
        hpCost: 50,
        effects: [{ kind: 'applyBuff', buffId: 'mighty', durationTicks: seconds(10) }],
    };
    // ---------------------------------------------------------------------------
    // archetype 4: Resource Conversion × 2 (HP↔Shield)
    // ---------------------------------------------------------------------------
    out['item.common.hp-to-shield'] = {
        id: 'item.common.hp-to-shield',
        name: 'HP to Shield Conversion',
        source: 'item',
        kind: 'reactive',
        cooldownTicks: 60,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onTakenDamage' },
        effects: [{ kind: 'shield', power: 0.1, durationTicks: seconds(8) }],
    };
    out['item.common.shield-to-hp'] = {
        id: 'item.common.shield-to-hp',
        name: 'Shield to HP Conversion',
        source: 'item',
        kind: 'reactive',
        cooldownTicks: 60,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onShieldBreak' },
        effects: [{ kind: 'heal', power: 0.2 }],
    };
    // ---------------------------------------------------------------------------
    // archetype 5: Stack Overflow Conversion × 7 (onMarkBurst で属性別 effect)
    //
    // §6.1 規約: archetype 5 は軸 1 mark-burst と連動して発火、stack 消費は軸 1 経路で完了。
    // 本軸は overflow 効果のみ発火 (heal / buff / shield 等、属性別)。
    // ---------------------------------------------------------------------------
    const overflowEffectByElement = {
        physical: { kind: 'heal', power: 0.1 },
        fire: { kind: 'applyDebuff', debuffId: 'burn', stacks: 1 },
        ice: { kind: 'shield', power: 0.1, durationTicks: seconds(8) },
        lightning: { kind: 'applyBuff', buffId: 'charge', stacks: 1 },
        earth: { kind: 'shield', power: 0.15, durationTicks: seconds(10) },
        light: { kind: 'heal', power: 0.15 },
        dark: { kind: 'applyDebuff', debuffId: 'poison', stacks: 1 },
    };
    for (const element of ELEMENTS_7) {
        out[`item.common.stack-overflow.${element}`] = {
            id: `item.common.stack-overflow.${element}`,
            name: `Stack Overflow: ${element}`,
            source: 'item',
            kind: 'trigger',
            cooldownTicks: 30,
            targeting: { shape: 'self' },
            activation: { onEvent: 'onMarkBurst' },
            effects: [overflowEffectByElement[element]],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 6: 回復循環 × 3
    // ---------------------------------------------------------------------------
    out['item.common.heal-cycle.on-hit'] = {
        id: 'item.common.heal-cycle.on-hit',
        name: 'Heal Cycle: on Hit',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onHit' },
        effects: [{ kind: 'heal', power: 0.02 }],
    };
    out['item.common.heal-cycle.on-taken-damage'] = {
        id: 'item.common.heal-cycle.on-taken-damage',
        name: 'Heal Cycle: on Taken Damage',
        source: 'item',
        kind: 'reactive',
        cooldownTicks: 30,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onTakenDamage' },
        effects: [{ kind: 'heal', power: 0.03 }],
    };
    out['item.common.heal-cycle.on-kill'] = {
        id: 'item.common.heal-cycle.on-kill',
        name: 'Heal Cycle: on Kill',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'heal', power: 0.1 }],
    };
    // ---------------------------------------------------------------------------
    // unique × 7
    // ---------------------------------------------------------------------------
    out['item.unique.unstoppable.kill-reset'] = {
        id: 'item.unique.unstoppable.kill-reset',
        name: 'Unstoppable: Kill Reset',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 60,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onKill' },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus', durationTicks: 1 }],
    };
    out['item.unique.berserker.hp-cost-amp'] = {
        id: 'item.unique.berserker.hp-cost-amp',
        name: 'Berserker: HP Cost Amp',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.unique.vampiric-blade.heal-on-hit'] = {
        id: 'item.unique.vampiric-blade.heal-on-hit',
        name: 'Vampiric Blade: Heal on Hit',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onHit' },
        effects: [{ kind: 'heal', power: 0.03 }],
    };
    out['item.unique.crystal-armor.shield-regen'] = {
        id: 'item.unique.crystal-armor.shield-regen',
        name: 'Crystal Armor: Shield Regen',
        source: 'item',
        kind: 'reactive',
        cooldownTicks: seconds(5),
        targeting: { shape: 'self' },
        activation: { onEvent: 'onShieldBreak' },
        effects: [{ kind: 'shield', power: 0.15, durationTicks: seconds(10) }],
    };
    out['item.unique.overflow-master.stack-amp'] = {
        id: 'item.unique.overflow-master.stack-amp',
        name: 'Overflow Master: Stack Amp',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.unique.eternal-cycle.full-reset'] = {
        id: 'item.unique.eternal-cycle.full-reset',
        name: 'Eternal Cycle: Full Reset',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(30),
        targeting: { shape: 'self' },
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus', durationTicks: 1 }],
    };
    // death-pact: HP 30% 以下の間ずっと有効な passive (draft §3、kind=passive)
    out['item.unique.death-pact.hp-sacrifice'] = {
        id: 'item.unique.death-pact.hp-sacrifice',
        name: 'Death Pact: HP Sacrifice',
        source: 'item',
        kind: 'passive',
        activation: { selfHpAtMost: 0.3 },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    return out;
})();
// =============================================================================
// Step 6.6 軸 8 射程・位置 (`.context/c5c_position_axis.md` v1)
//
// 28 個 = common 21 + unique 7。「どこで / どの距離で戦う」を Activation 4 フィールド
// (requiresDistance / requiresPositionRelative / requiresEnemyCountInRadius /
// requiresBehindTarget) で表現。
//
// archetype 別 (§2.1):
//   1. 距離帯依存ダメ × 3 (near / mid / far、passive、requiresDistance)
//   2. 背面攻撃 × 1 (passive、requiresBehindTarget)
//   3. 敵密度依存 × 2 (surrounded / isolated、passive、requiresEnemyCountInRadius)
//   4. Placeable 周辺 aura × 3 (attack/defense/utility、requiresPositionRelative reference:placeable)
//   5. 召喚体周辺 aura × 3 (attack/defense/utility、requiresPositionRelative reference:summon)
//   6. Caster-Target thread (line aura) × 1 (passive)
//   7. 印再配分 (Mark Spread) × 7 (属性別 trigger、onHit + iCD 30)
//   8. 被弾距離依存 × 1 (reactive、onTakenDamage + requiresDistance)
//
// 数値 (§5):
//   - 距離帯: near ≤3m / mid 3<d≤10m / far >10m
//   - aura 半径: 3〜7m (Tier 個体差)
//   - 敵密度: 包囲 = 5m 内 ≥3 / 孤立 = 10m 内 =1
//   - 背面: halfAngleDegrees=45 (合計 90 度)
// =============================================================================
const POSITION_AXIS_8 = (() => {
    const out = {};
    // ---------------------------------------------------------------------------
    // archetype 1: 距離帯依存ダメ × 3 (passive + requiresDistance、近似 stat-boost)
    // ---------------------------------------------------------------------------
    out['item.common.distance-band.near'] = {
        id: 'item.common.distance-band.near',
        name: 'Distance Band: Near (≤3m)',
        source: 'item',
        kind: 'passive',
        activation: { requiresDistance: { max: 3 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.common.distance-band.mid'] = {
        id: 'item.common.distance-band.mid',
        name: 'Distance Band: Mid (3-10m)',
        source: 'item',
        kind: 'passive',
        activation: { requiresDistance: { min: 3, max: 10 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.common.distance-band.far'] = {
        id: 'item.common.distance-band.far',
        name: 'Distance Band: Far (>10m)',
        source: 'item',
        kind: 'passive',
        activation: { requiresDistance: { min: 10 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    // ---------------------------------------------------------------------------
    // archetype 2: 背面攻撃 × 1 (passive + requiresBehindTarget)
    // ---------------------------------------------------------------------------
    out['item.common.backstab'] = {
        id: 'item.common.backstab',
        name: 'Backstab Bonus',
        source: 'item',
        kind: 'passive',
        activation: { requiresBehindTarget: { halfAngleDegrees: 45 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    // ---------------------------------------------------------------------------
    // archetype 3: 敵密度依存 × 2 (surrounded / isolated)
    // ---------------------------------------------------------------------------
    out['item.common.crowd-density.surrounded'] = {
        id: 'item.common.crowd-density.surrounded',
        name: 'Crowd Density: Surrounded',
        source: 'item',
        kind: 'passive',
        activation: { requiresEnemyCountInRadius: { radius: 5, minCount: 3 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.common.crowd-density.isolated'] = {
        id: 'item.common.crowd-density.isolated',
        name: 'Crowd Density: Isolated',
        source: 'item',
        kind: 'passive',
        activation: { requiresEnemyCountInRadius: { radius: 10, maxCount: 1 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    // ---------------------------------------------------------------------------
    // archetype 4: Placeable 周辺 aura × 3
    // ---------------------------------------------------------------------------
    for (const tag of ['attack', 'defense', 'utility']) {
        out[`item.common.placeable-aura.${tag}`] = {
            id: `item.common.placeable-aura.${tag}`,
            name: `Placeable Aura: ${tag}`,
            source: 'item',
            kind: 'passive',
            activation: { requiresPositionRelative: { reference: 'placeable', maxDistance: 5 } },
            effects: [
                {
                    kind: 'applyBuff',
                    buffId: tag === 'attack'
                        ? 'stat-boost.atk-bonus'
                        : tag === 'defense'
                            ? 'stat-boost.armor'
                            : 'stat-boost.move-speed',
                },
            ],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 5: 召喚体周辺 aura × 3
    // ---------------------------------------------------------------------------
    for (const tag of ['attack', 'defense', 'utility']) {
        out[`item.common.summon-aura.${tag}`] = {
            id: `item.common.summon-aura.${tag}`,
            name: `Summon Aura: ${tag}`,
            source: 'item',
            kind: 'passive',
            activation: { requiresPositionRelative: { reference: 'summon', maxDistance: 5 } },
            effects: [
                {
                    kind: 'applyBuff',
                    buffId: tag === 'attack'
                        ? 'stat-boost.atk-bonus'
                        : tag === 'defense'
                            ? 'stat-boost.armor'
                            : 'stat-boost.move-speed',
                },
            ],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 6: Caster-Target thread × 1
    // ---------------------------------------------------------------------------
    out['item.common.caster-target-thread'] = {
        id: 'item.common.caster-target-thread',
        name: 'Caster-Target Thread',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    // ---------------------------------------------------------------------------
    // archetype 7: 印再配分 (Mark Spread) × 7 (属性別 trigger onHit + iCD 30)
    // ---------------------------------------------------------------------------
    for (const element of ELEMENTS_7) {
        out[`item.common.mark-spread.${element}`] = {
            id: `item.common.mark-spread.${element}`,
            name: `Mark Spread: ${element}`,
            source: 'item',
            kind: 'trigger',
            cooldownTicks: 30,
            range: 5,
            targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
            activation: {
                onEvent: 'onHit',
                targetHasStatusAtLeast: { statusId: markId(element), stacks: 1 },
            },
            effects: [{ kind: 'applyDebuff', debuffId: markId(element), stacks: 1 }],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 8: 被弾距離依存 × 1 (reactive + onTakenDamage + requiresDistance)
    // ---------------------------------------------------------------------------
    out['item.common.hit-range-reactive'] = {
        id: 'item.common.hit-range-reactive',
        name: 'Hit Range Reactive',
        source: 'item',
        kind: 'reactive',
        cooldownTicks: seconds(2),
        targeting: { shape: 'self' },
        activation: { onEvent: 'onTakenDamage', requiresDistance: { min: 8 } },
        effects: [
            { kind: 'applyBuff', buffId: 'stat-boost.dodge', durationTicks: seconds(3) },
        ],
    };
    // ---------------------------------------------------------------------------
    // unique × 7
    // ---------------------------------------------------------------------------
    out['item.unique.assassin-cloak.backstab-amp'] = {
        id: 'item.unique.assassin-cloak.backstab-amp',
        name: 'Assassin Cloak: Backstab Amp',
        source: 'item',
        kind: 'passive',
        activation: { requiresBehindTarget: { halfAngleDegrees: 45 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.unique.crowd-controller.surrounded-amp'] = {
        id: 'item.unique.crowd-controller.surrounded-amp',
        name: 'Crowd Controller: Surrounded Amp',
        source: 'item',
        kind: 'passive',
        activation: { requiresEnemyCountInRadius: { radius: 5, minCount: 3 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.unique.lone-wolf.isolated-cd'] = {
        id: 'item.unique.lone-wolf.isolated-cd',
        name: 'Lone Wolf: Isolated CD',
        source: 'item',
        kind: 'passive',
        activation: { requiresEnemyCountInRadius: { radius: 10, maxCount: 1 } },
        effects: [],
        cdrRate: 0.05,
    };
    out['item.unique.placeable-conductor.aura-extend'] = {
        id: 'item.unique.placeable-conductor.aura-extend',
        name: 'Placeable Conductor: Aura Extend',
        source: 'item',
        kind: 'passive',
        activation: { requiresPositionRelative: { reference: 'placeable', maxDistance: 10 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.unique.summon-vanguard.line-formation'] = {
        id: 'item.unique.summon-vanguard.line-formation',
        name: 'Summon Vanguard: Line Formation',
        source: 'item',
        kind: 'passive',
        activation: { requiresPositionRelative: { reference: 'summon', maxDistance: 10 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.unique.mark-spreader.contagion'] = {
        id: 'item.unique.mark-spreader.contagion',
        name: 'Mark Spreader: Contagion',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: { onEvent: 'onHit' },
        effects: [{ kind: 'applyDebuff', debuffId: 'mark.physical', stacks: 1 }],
    };
    out['item.unique.position-master.distance-flexibility'] = {
        id: 'item.unique.position-master.distance-flexibility',
        name: 'Position Master: Distance Flexibility',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    return out;
})();
// =============================================================================
// Step 6.7 軸 5 行動ルール (`.context/c5c_behavior_axis.md` v1)
//
// 31 個 = common 23 + unique 8。docs/04 §4.2.1 既存 41 行動ルールに対する
// 「装備のセットアップ自体に反応する」装備スキル群。
// `BehaviorCoreCategory` (6 core カテゴリ、shared で PR #27 導入済) のみ参照。
//
// archetype 別 (§2.1):
//   1. Targeting Synergy × 3 (target_lowest_hp / target_attacker / target_afflicted)
//   2. Movement Synergy × 3 (move_melee_stick / move_distance_long / move_kite_circle)
//   3. Tempo Synergy × 4 (tempo_burst / tempo_executor / tempo_conditional / tempo_dot_focus)
//   4. Threat Synergy × 2 (threat_high / threat_defender)
//   5. Position Synergy × 2 (pos_behind_target / pos_safe_line)
//   6. Reactive Synergy × 2 (react_hp25_lastditch / react_focused_hide)、trigger
//   7. Build Variety Boost × 1 (requiresBehaviorRuleCount)
//   8. Combo Boost × 3 (requiresBehaviorRuleCombo)
//   9. Tradeoff Keystone × 3 (offensive / defensive / utility)
// =============================================================================
const BEHAVIOR_AXIS_5 = (() => {
    const out = {};
    // ---------------------------------------------------------------------------
    // archetype 1: Targeting Synergy × 3
    // ---------------------------------------------------------------------------
    const targetingRules = [
        ['target-lowest-hp', 'target_lowest_hp'],
        ['target-attacker', 'target_attacker'],
        ['target-afflicted', 'target_afflicted'],
    ];
    for (const [keyword, ruleId] of targetingRules) {
        out[`item.common.targeting-synergy.${keyword}`] = {
            id: `item.common.targeting-synergy.${keyword}`,
            name: `Targeting Synergy: ${keyword}`,
            source: 'item',
            kind: 'passive',
            activation: { requiresBehaviorRule: { category: 'targeting', ruleId } },
            effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 2: Movement Synergy × 3
    // ---------------------------------------------------------------------------
    const movementRules = [
        ['melee-stick', 'move_melee_stick'],
        ['distance-long', 'move_distance_long'],
        ['kite-circle', 'move_kite_circle'],
    ];
    for (const [keyword, ruleId] of movementRules) {
        out[`item.common.movement-synergy.${keyword}`] = {
            id: `item.common.movement-synergy.${keyword}`,
            name: `Movement Synergy: ${keyword}`,
            source: 'item',
            kind: 'passive',
            activation: { requiresBehaviorRule: { category: 'movement', ruleId } },
            effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 3: Tempo Synergy × 4
    // ---------------------------------------------------------------------------
    const tempoRules = [
        ['burst', 'tempo_burst'],
        ['executor', 'tempo_executor'],
        ['conditional', 'tempo_conditional'],
        ['dot-focus', 'tempo_dot_focus'],
    ];
    for (const [keyword, ruleId] of tempoRules) {
        out[`item.common.tempo-synergy.${keyword}`] = {
            id: `item.common.tempo-synergy.${keyword}`,
            name: `Tempo Synergy: ${keyword}`,
            source: 'item',
            kind: 'passive',
            activation: { requiresBehaviorRule: { category: 'tempo', ruleId } },
            effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 4: Threat Synergy × 2
    // ---------------------------------------------------------------------------
    for (const [keyword, ruleId] of [
        ['threat-high', 'threat_high'],
        ['threat-defender', 'threat_defender'],
    ]) {
        out[`item.common.threat-synergy.${keyword}`] = {
            id: `item.common.threat-synergy.${keyword}`,
            name: `Threat Synergy: ${keyword}`,
            source: 'item',
            kind: 'passive',
            activation: { requiresBehaviorRule: { category: 'threatPolicy', ruleId } },
            effects: [{ kind: 'applyBuff', buffId: 'stat-boost.armor' }],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 5: Position Synergy × 2
    // ---------------------------------------------------------------------------
    for (const [keyword, ruleId] of [
        ['behind-target', 'pos_behind_target'],
        ['safe-line', 'pos_safe_line'],
    ]) {
        out[`item.common.position-synergy.${keyword}`] = {
            id: `item.common.position-synergy.${keyword}`,
            name: `Position Synergy: ${keyword}`,
            source: 'item',
            kind: 'passive',
            activation: { requiresBehaviorRule: { category: 'positionPolicy', ruleId } },
            effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 6: Reactive Synergy × 2 (DamageReact 連携 trigger)
    // ---------------------------------------------------------------------------
    for (const [keyword, ruleId] of [
        ['hp25-lastditch', 'react_hp25_lastditch'],
        ['focused-hide', 'react_focused_hide'],
    ]) {
        out[`item.common.reactive-synergy.${keyword}`] = {
            id: `item.common.reactive-synergy.${keyword}`,
            name: `Reactive Synergy: ${keyword}`,
            source: 'item',
            kind: 'trigger',
            cooldownTicks: seconds(5),
            targeting: { shape: 'self' },
            activation: {
                onEvent: 'onCrit',
                requiresBehaviorRule: { category: 'damageReact', ruleId },
            },
            effects: [
                { kind: 'applyBuff', buffId: 'stat-boost.atk-bonus', durationTicks: seconds(5) },
            ],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 7: Build Variety Boost × 1 (requiresBehaviorRuleCount)
    // ---------------------------------------------------------------------------
    out['item.common.rule-count-boost'] = {
        id: 'item.common.rule-count-boost',
        name: 'Rule Count Boost',
        source: 'item',
        kind: 'passive',
        activation: { requiresBehaviorRuleCount: { min: 4 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    // ---------------------------------------------------------------------------
    // archetype 8: Combo Boost × 3
    // ---------------------------------------------------------------------------
    out['item.common.combo-boost.targeting-tempo'] = {
        id: 'item.common.combo-boost.targeting-tempo',
        name: 'Combo Boost: Targeting + Tempo',
        source: 'item',
        kind: 'passive',
        activation: {
            requiresBehaviorRuleCombo: [
                { category: 'targeting', ruleId: 'target_lowest_hp' },
                { category: 'tempo', ruleId: 'tempo_executor' },
            ],
        },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.common.combo-boost.movement-position'] = {
        id: 'item.common.combo-boost.movement-position',
        name: 'Combo Boost: Movement + Position',
        source: 'item',
        kind: 'passive',
        activation: {
            requiresBehaviorRuleCombo: [
                { category: 'movement', ruleId: 'move_melee_stick' },
                { category: 'positionPolicy', ruleId: 'pos_behind_target' },
            ],
        },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.common.combo-boost.threat-react'] = {
        id: 'item.common.combo-boost.threat-react',
        name: 'Combo Boost: Threat + DamageReact',
        source: 'item',
        kind: 'passive',
        activation: {
            requiresBehaviorRuleCombo: [
                { category: 'threatPolicy', ruleId: 'threat_high' },
                { category: 'damageReact', ruleId: 'react_hp25_lastditch' },
            ],
        },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.armor' }],
    };
    // ---------------------------------------------------------------------------
    // archetype 9: Tradeoff Keystone × 3 (offensive / defensive / utility)
    //
    // 強化 + 制約を同時付与 (catalogue 側は flag、制約適用は sim 拡張)
    // ---------------------------------------------------------------------------
    out['item.common.tradeoff-keystone.offensive'] = {
        id: 'item.common.tradeoff-keystone.offensive',
        name: 'Tradeoff Keystone: Offensive',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.common.tradeoff-keystone.defensive'] = {
        id: 'item.common.tradeoff-keystone.defensive',
        name: 'Tradeoff Keystone: Defensive',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.armor' }],
    };
    out['item.common.tradeoff-keystone.utility'] = {
        id: 'item.common.tradeoff-keystone.utility',
        name: 'Tradeoff Keystone: Utility',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.move-speed' }],
    };
    // ---------------------------------------------------------------------------
    // unique × 8
    // ---------------------------------------------------------------------------
    out['item.unique.executioner.hp30-burst'] = {
        id: 'item.unique.executioner.hp30-burst',
        name: 'Executioner: HP30 Burst',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(15),
        range: 8,
        targeting: { shape: 'singleTarget' },
        activation: {
            targetHpAtMost: 0.3,
            requiresBehaviorRuleCombo: [
                { category: 'targeting', ruleId: 'target_lowest_hp' },
                { category: 'tempo', ruleId: 'tempo_executor' },
            ],
        },
        effects: [{ kind: 'damage', element: 'weapon-derived', power: 1.5 }],
    };
    out['item.unique.shadow-step.behind-strike'] = {
        id: 'item.unique.shadow-step.behind-strike',
        name: 'Shadow Step: Behind Strike',
        source: 'item',
        kind: 'passive',
        activation: {
            requiresBehindTarget: { halfAngleDegrees: 45 },
            requiresBehaviorRule: { category: 'movement', ruleId: 'move_melee_stick' },
        },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.unique.kite-master.distance-burn'] = {
        id: 'item.unique.kite-master.distance-burn',
        name: 'Kite Master: Distance Burn',
        source: 'item',
        kind: 'passive',
        activation: {
            requiresBehaviorRule: { category: 'movement', ruleId: 'move_distance_long' },
        },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.unique.guardian-oath.tank-reflect'] = {
        id: 'item.unique.guardian-oath.tank-reflect',
        name: 'Guardian Oath: Tank Reflect',
        source: 'item',
        kind: 'reactive',
        cooldownTicks: seconds(10),
        targeting: { shape: 'circle', radius: 5, maxTargets: 10 },
        activation: {
            onEvent: 'onShieldBreak',
            requiresBehaviorRule: { category: 'threatPolicy', ruleId: 'threat_high' },
        },
        effects: [{ kind: 'damage', element: 'physical', power: 1.0 }],
    };
    out['item.unique.dot-cultivator.afflicted-amp'] = {
        id: 'item.unique.dot-cultivator.afflicted-amp',
        name: 'DoT Cultivator: Afflicted Amp',
        source: 'item',
        kind: 'passive',
        activation: {
            requiresBehaviorRule: { category: 'targeting', ruleId: 'target_afflicted' },
        },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.unique.versatile-master.rule-count-amp'] = {
        id: 'item.unique.versatile-master.rule-count-amp',
        name: 'Versatile Master: Rule Count Amp',
        source: 'item',
        kind: 'passive',
        activation: { requiresBehaviorRuleCount: { min: 6 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    // kind 選定ルール: onHpBelow は被害イベント = reactive 必須 (ADR 0013 §2.2.1)
    out['item.unique.adaptive-armor.react-buff-chain'] = {
        id: 'item.unique.adaptive-armor.react-buff-chain',
        name: 'Adaptive Armor: React Buff Chain',
        source: 'item',
        kind: 'reactive',
        cooldownTicks: seconds(10),
        targeting: { shape: 'allAllies' },
        activation: { onEvent: 'onHpBelow' },
        effects: [
            { kind: 'applyBuff', buffId: 'stat-boost.armor', durationTicks: seconds(5) },
        ],
    };
    out['item.unique.combo-master.three-way-link'] = {
        id: 'item.unique.combo-master.three-way-link',
        name: 'Combo Master: Three-Way Link',
        source: 'item',
        kind: 'passive',
        activation: {
            requiresBehaviorRuleCombo: [
                { category: 'targeting', ruleId: 'target_lowest_hp' },
                { category: 'movement', ruleId: 'move_melee_stick' },
                { category: 'tempo', ruleId: 'tempo_burst' },
            ],
        },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    return out;
})();
// =============================================================================
// Step 6.8 軸 3 設置物連携 (`.context/c5c_placeable_axis.md` v1)
//
// 52 個 = common 41 + unique 11。Placeable 5 trigger kind (contact / tick-pulse /
// aura-zone / projectile-shield / manual) を catalogue で活用。manual は common
// 不在 = unique 2 個のみで提供。
//
// archetype 別 (§2.1):
//   1. 接触罠配置 × 5 (physical/fire/ice/lightning/dark、active + contact placeable)
//   2. 砲台配置 × 5 (fire/ice/lightning/light/dark、active + tick-pulse placeable)
//   3. オーラ場配置 × 4 (light/earth/dark/fire、active + aura-zone placeable)
//   4. バリケード配置 × 4 (physical/earth/ice/light、active + projectile-shield placeable)
//   5. 発火時バフ × 7 (全属性、trigger + onPlaceableTrigger + applyBuff)
//   6. 存続中強化 × 7 (全属性、passive、近似 stat-boost flag)
//   7. 連鎖配置 × 1 (trigger + onPlaceableTrigger + placePlaceable chain)
//   8. placeable リチャージ × 1 (reactive + onPlaceableEnd + flag)
//   9. 印交差 × 7 (全属性、trigger + onPlaceableTrigger + applyDebuff mark.<element>)
// =============================================================================
const PLACEABLE_AXIS_3 = (() => {
    const out = {};
    // ---------------------------------------------------------------------------
    // archetype 1: 接触罠配置 × 5 (active + contact placeable)
    // ---------------------------------------------------------------------------
    const contactElements = ['physical', 'fire', 'ice', 'lightning', 'dark'];
    for (const element of contactElements) {
        out[`item.common.contact-trap.${element}`] = {
            id: `item.common.contact-trap.${element}`,
            name: `Contact Trap: ${element}`,
            source: 'item',
            kind: 'active',
            cooldownTicks: seconds(15),
            targeting: { shape: 'self' },
            activation: {},
            effects: [
                {
                    kind: 'placePlaceable',
                    spec: {
                        templateId: `contact-trap.${element}`,
                        radius: 1,
                        durationTicks: seconds(20),
                        maxConcurrentPerOwner: 3,
                        isWalkable: true,
                        trigger: { kind: 'contact', consumesOnTrigger: true },
                        onTriggerEffects: [
                            { kind: 'damage', element: element, power: 1.0 },
                        ],
                    },
                },
            ],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 2: 砲台配置 × 5 (active + tick-pulse placeable)
    // ---------------------------------------------------------------------------
    const tickElements = ['fire', 'ice', 'lightning', 'light', 'dark'];
    for (const element of tickElements) {
        out[`item.common.tick-turret.${element}`] = {
            id: `item.common.tick-turret.${element}`,
            name: `Tick Turret: ${element}`,
            source: 'item',
            kind: 'active',
            cooldownTicks: seconds(30),
            targeting: { shape: 'self' },
            activation: {},
            effects: [
                {
                    kind: 'placePlaceable',
                    spec: {
                        templateId: `tick-turret.${element}`,
                        radius: 5,
                        durationTicks: seconds(20),
                        maxConcurrentPerOwner: 2,
                        isWalkable: false,
                        trigger: { kind: 'tick-pulse', periodTicks: seconds(2) },
                        onTriggerEffects: [
                            { kind: 'damage', element: element, power: 0.5 },
                        ],
                    },
                },
            ],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 3: オーラ場配置 × 4 (active + aura-zone placeable)
    // ---------------------------------------------------------------------------
    const auraElements = ['light', 'earth', 'dark', 'fire'];
    for (const element of auraElements) {
        out[`item.common.aura-zone.${element}`] = {
            id: `item.common.aura-zone.${element}`,
            name: `Aura Zone: ${element}`,
            source: 'item',
            kind: 'active',
            cooldownTicks: seconds(30),
            targeting: { shape: 'self' },
            activation: {},
            effects: [
                {
                    kind: 'placePlaceable',
                    spec: {
                        templateId: `aura-zone.${element}`,
                        radius: 5,
                        durationTicks: seconds(15),
                        maxConcurrentPerOwner: 2,
                        isWalkable: true,
                        trigger: { kind: 'aura-zone' },
                        onTriggerEffects: element === 'light' || element === 'earth'
                            ? [{ kind: 'heal', power: 0.05 }]
                            : [{ kind: 'damage', element: element, power: 0.3 }],
                    },
                },
            ],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 4: バリケード配置 × 4 (active + projectile-shield placeable)
    // ---------------------------------------------------------------------------
    const shieldElements = ['physical', 'earth', 'ice', 'light'];
    for (const element of shieldElements) {
        out[`item.common.projectile-shield.${element}`] = {
            id: `item.common.projectile-shield.${element}`,
            name: `Projectile Shield: ${element}`,
            source: 'item',
            kind: 'active',
            cooldownTicks: seconds(15),
            targeting: { shape: 'self' },
            activation: {},
            effects: [
                {
                    kind: 'placePlaceable',
                    spec: {
                        templateId: `projectile-shield.${element}`,
                        radius: 3,
                        durationTicks: seconds(20),
                        maxConcurrentPerOwner: 2,
                        isWalkable: false,
                        hp: 300,
                        trigger: { kind: 'projectile-shield', missChanceBonus: 0.2 },
                        onTriggerEffects: [],
                    },
                },
            ],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 5: 発火時バフ × 7 (trigger + onPlaceableTrigger + applyBuff)
    // ---------------------------------------------------------------------------
    for (const element of ELEMENTS_7) {
        out[`item.common.placeable-trigger-buff.${element}`] = {
            id: `item.common.placeable-trigger-buff.${element}`,
            name: `Placeable Trigger Buff: ${element}`,
            source: 'item',
            kind: 'trigger',
            cooldownTicks: 30,
            targeting: { shape: 'self' },
            activation: { onEvent: 'onPlaceableTrigger' },
            effects: [
                { kind: 'applyBuff', buffId: 'stat-boost.atk-bonus', durationTicks: seconds(5) },
            ],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 6: 存続中強化 × 7 (passive、近似 stat-boost flag)
    // ---------------------------------------------------------------------------
    for (const element of ELEMENTS_7) {
        out[`item.common.placeable-sustain-boost.${element}`] = {
            id: `item.common.placeable-sustain-boost.${element}`,
            name: `Placeable Sustain Boost: ${element}`,
            source: 'item',
            kind: 'passive',
            activation: {},
            effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 7: 連鎖配置 × 1 (trigger + onPlaceableTrigger + placePlaceable)
    // ---------------------------------------------------------------------------
    out['item.common.chain-placement'] = {
        id: 'item.common.chain-placement',
        name: 'Chain Placement',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onPlaceableTrigger' },
        effects: [
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'chain-placement.minor',
                    radius: 1,
                    durationTicks: seconds(10),
                    maxConcurrentPerOwner: 3,
                    isWalkable: true,
                    trigger: { kind: 'contact', consumesOnTrigger: true },
                    onTriggerEffects: [{ kind: 'damage', element: 'physical', power: 0.5 }],
                },
            },
        ],
    };
    // ---------------------------------------------------------------------------
    // archetype 8: placeable リチャージ × 1 (trigger + onPlaceableEnd、ADR 0013 §2.2.1
    // kind 選定ルール遵守: onPlaceable* は被害イベントではない = trigger)
    // ---------------------------------------------------------------------------
    out['item.common.placeable-recharge'] = {
        id: 'item.common.placeable-recharge',
        name: 'Placeable Recharge',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onPlaceableEnd' },
        effects: [
            { kind: 'applyBuff', buffId: 'stat-boost.atk-bonus', durationTicks: seconds(3) },
        ],
    };
    // ---------------------------------------------------------------------------
    // archetype 9: 印交差 × 7 (trigger + onPlaceableTrigger + applyDebuff mark.<element>)
    // ---------------------------------------------------------------------------
    for (const element of ELEMENTS_7) {
        out[`item.common.mark-imprint-on-trigger.${element}`] = {
            id: `item.common.mark-imprint-on-trigger.${element}`,
            name: `Mark Imprint on Trigger: ${element}`,
            source: 'item',
            kind: 'trigger',
            cooldownTicks: 30,
            targeting: { shape: 'singleTarget' },
            activation: { onEvent: 'onPlaceableTrigger' },
            effects: [{ kind: 'applyDebuff', debuffId: markId(element), stacks: 1 }],
        };
    }
    // ---------------------------------------------------------------------------
    // unique × 11 (§3)
    // ---------------------------------------------------------------------------
    out['item.unique.flame-garden.tick-aoe'] = {
        id: 'item.unique.flame-garden.tick-aoe',
        name: 'Flame Garden: Tick AoE',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(30),
        targeting: { shape: 'self' },
        activation: {},
        effects: [
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'flame-garden',
                    radius: 5,
                    durationTicks: seconds(20),
                    maxConcurrentPerOwner: 1,
                    isWalkable: true,
                    trigger: { kind: 'tick-pulse', periodTicks: seconds(2) },
                    onTriggerEffects: [{ kind: 'damage', element: 'fire', power: 0.7 }],
                },
            },
        ],
    };
    out['item.unique.frost-prison.contact-freeze'] = {
        id: 'item.unique.frost-prison.contact-freeze',
        name: 'Frost Prison: Contact Freeze',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(20),
        targeting: { shape: 'self' },
        activation: {},
        effects: [
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'frost-prison',
                    radius: 1,
                    durationTicks: seconds(20),
                    maxConcurrentPerOwner: 3,
                    isWalkable: true,
                    trigger: { kind: 'contact', consumesOnTrigger: true },
                    onTriggerEffects: [
                        { kind: 'damage', element: 'ice', power: 0.5 },
                        { kind: 'applyDebuff', debuffId: 'freeze', stacks: 1 },
                    ],
                },
            },
        ],
    };
    out['item.unique.thunder-pylon.manual-chain'] = {
        id: 'item.unique.thunder-pylon.manual-chain',
        name: 'Thunder Pylon: Manual Chain',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(30),
        targeting: { shape: 'self' },
        activation: {},
        effects: [
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'thunder-pylon',
                    radius: 3,
                    durationTicks: seconds(30),
                    maxConcurrentPerOwner: 3,
                    isWalkable: false,
                    trigger: {
                        kind: 'manual',
                        linkSkillId: 'item.unique.detonator-cascade',
                    },
                    onTriggerEffects: [{ kind: 'damage', element: 'lightning', power: 1.5 }],
                },
            },
        ],
    };
    out['item.unique.stone-bastion.shield-link'] = {
        id: 'item.unique.stone-bastion.shield-link',
        name: 'Stone Bastion: Shield Link',
        source: 'item',
        kind: 'passive',
        activation: { requiresPositionRelative: { reference: 'placeable', maxDistance: 5 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.armor' }],
    };
    out['item.unique.light-grove.aura-share'] = {
        id: 'item.unique.light-grove.aura-share',
        name: 'Light Grove: Aura Share',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(30),
        targeting: { shape: 'self' },
        activation: {},
        effects: [
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'light-grove',
                    radius: 7,
                    durationTicks: seconds(20),
                    maxConcurrentPerOwner: 1,
                    isWalkable: true,
                    trigger: { kind: 'aura-zone' },
                    onTriggerEffects: [{ kind: 'heal', power: 0.05 }],
                },
            },
        ],
    };
    out['item.unique.shadow-totem.dark-aura'] = {
        id: 'item.unique.shadow-totem.dark-aura',
        name: 'Shadow Totem: Dark Aura',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(30),
        targeting: { shape: 'self' },
        activation: {},
        effects: [
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'shadow-totem',
                    radius: 5,
                    durationTicks: seconds(20),
                    maxConcurrentPerOwner: 1,
                    isWalkable: true,
                    trigger: { kind: 'aura-zone' },
                    onTriggerEffects: [{ kind: 'applyDebuff', debuffId: 'poison', stacks: 1 }],
                },
            },
        ],
    };
    out['item.unique.detonator-cascade'] = {
        id: 'item.unique.detonator-cascade',
        name: 'Detonator Cascade',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(10),
        targeting: { shape: 'self' },
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus', durationTicks: seconds(3) }],
    };
    out['item.unique.placeable-fortress.cap-extend'] = {
        id: 'item.unique.placeable-fortress.cap-extend',
        name: 'Placeable Fortress: Cap Extend',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.armor' }],
    };
    out['item.unique.ephemeral-spike.short-cd'] = {
        id: 'item.unique.ephemeral-spike.short-cd',
        name: 'Ephemeral Spike: Short CD',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [],
        cdrRate: 0.04,
    };
    out['item.unique.tripwire-volley.contact-aoe'] = {
        id: 'item.unique.tripwire-volley.contact-aoe',
        name: 'Tripwire Volley: Contact AoE',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(15),
        targeting: { shape: 'self' },
        activation: {},
        effects: [
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'tripwire-volley',
                    radius: 4,
                    durationTicks: seconds(20),
                    maxConcurrentPerOwner: 3,
                    isWalkable: true,
                    trigger: { kind: 'contact', consumesOnTrigger: true },
                    onTriggerEffects: [{ kind: 'damage', element: 'dark', power: 0.8 }],
                },
            },
        ],
    };
    out['item.unique.repeater-turret.fast-tick'] = {
        id: 'item.unique.repeater-turret.fast-tick',
        name: 'Repeater Turret: Fast Tick',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(30),
        targeting: { shape: 'self' },
        activation: {},
        effects: [
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'repeater-turret',
                    radius: 5,
                    durationTicks: seconds(20),
                    maxConcurrentPerOwner: 1,
                    isWalkable: false,
                    trigger: { kind: 'tick-pulse', periodTicks: 30 },
                    onTriggerEffects: [{ kind: 'damage', element: 'lightning', power: 0.4 }],
                },
            },
        ],
    };
    return out;
})();
// =============================================================================
// Step 6.9 軸 4 召喚連携 (`.context/c5c_summon_axis.md` v1)
//
// 53 個 = common 42 + unique 11。ADR 0009 既存 SummonSpec を catalogue 側で活用。
// unitId 仮 ID は `unit.<species>.<element>` 形式 (§2.4)、unit spec 詳細は別 ADR。
//
// archetype 別 (§2.1):
//   1. 属性召喚 active × 7 (全 7 属性、active + summon)
//   2. 召喚数依存バフ × 7 (全 7 属性、passive、近似 stat-boost flag)
//   3. 召喚体存命中強化 × 7 (全 7 属性、passive)
//   4. onDeath 連鎖 × 3 (fire/lightning/dark、trigger + onSummonDied)
//   5. 召喚体補充 × 1 (trigger + onSummonDied、kind 選定ルール: onSummon* は
//      被害イベントではない = trigger 分類、draft v1 reactive 表記は pre-ADR-0013)
//   6. 召喚 × 印交差 × 7 (全 7 属性、trigger + onSummonAttack + applyDebuff mark)
//   7. 召喚体属性付与 × 7 (全 7 属性、passive、近似 stat-boost flag)
//   8. thread aura 借用 × 3 (dark/light/earth、passive 弱体版)
// =============================================================================
const SUMMON_UNIT_ID_BY_ELEMENT = {
    physical: 'unit.beast.physical',
    fire: 'unit.fire-spirit.fire',
    ice: 'unit.frost-spirit.ice',
    lightning: 'unit.thunder-elemental.lightning',
    earth: 'unit.golem.earth',
    light: 'unit.angel.light',
    dark: 'unit.skeleton.dark',
};
const SUMMON_AXIS_4 = (() => {
    const out = {};
    // ---------------------------------------------------------------------------
    // archetype 1: 属性召喚 active × 7
    // ---------------------------------------------------------------------------
    for (const element of ELEMENTS_7) {
        const unitId = SUMMON_UNIT_ID_BY_ELEMENT[element];
        out[`item.common.summon-element.${element}`] = {
            id: `item.common.summon-element.${element}`,
            name: `Summon Element: ${element}`,
            source: 'item',
            kind: 'active',
            cooldownTicks: seconds(60),
            targeting: { shape: 'self' },
            activation: {},
            effects: [
                {
                    kind: 'summon',
                    spec: {
                        unitId,
                        durationTicks: seconds(30),
                        count: element === 'dark' ? 3 : 1,
                        maxConcurrentPerOwner: 3,
                    },
                },
            ],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 2: 召喚数依存バフ × 7 (passive、近似 stat-boost flag)
    // ---------------------------------------------------------------------------
    for (const element of ELEMENTS_7) {
        out[`item.common.summon-count-boost.${element}`] = {
            id: `item.common.summon-count-boost.${element}`,
            name: `Summon Count Boost: ${element}`,
            source: 'item',
            kind: 'passive',
            activation: {},
            effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 3: 召喚体存命中強化 × 7 (passive)
    // ---------------------------------------------------------------------------
    for (const element of ELEMENTS_7) {
        out[`item.common.active-summon-boost.${element}`] = {
            id: `item.common.active-summon-boost.${element}`,
            name: `Active Summon Boost: ${element}`,
            source: 'item',
            kind: 'passive',
            activation: {},
            effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 4: onDeath 連鎖 × 3 (fire/lightning/dark)
    // ---------------------------------------------------------------------------
    for (const element of ['fire', 'lightning', 'dark']) {
        out[`item.common.death-cascade.${element}`] = {
            id: `item.common.death-cascade.${element}`,
            name: `Death Cascade: ${element}`,
            source: 'item',
            kind: 'trigger',
            cooldownTicks: 30,
            range: 5,
            targeting: { shape: 'circle', radius: 5, maxTargets: 8 },
            activation: { onEvent: 'onSummonDied' },
            effects: [{ kind: 'damage', element: element, power: 1.0 }],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 5: 召喚体補充 × 1 (trigger + onSummonDied、近似 flag)
    //
    // draft v1 §2.1 表記は reactive だが、ADR 0013 §2.2.1 kind 選定ルール厳格解釈で
    // onSummon* は被害イベントではない = trigger 分類が正しい。
    // ---------------------------------------------------------------------------
    out['item.common.summon-replenish'] = {
        id: 'item.common.summon-replenish',
        name: 'Summon Replenish',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onSummonDied' },
        effects: [
            { kind: 'applyBuff', buffId: 'stat-boost.atk-bonus', durationTicks: seconds(3) },
        ],
    };
    // ---------------------------------------------------------------------------
    // archetype 6: 召喚 × 印交差 × 7 (trigger + onSummonAttack + applyDebuff mark)
    // ---------------------------------------------------------------------------
    for (const element of ELEMENTS_7) {
        out[`item.common.summon-mark-imprint.${element}`] = {
            id: `item.common.summon-mark-imprint.${element}`,
            name: `Summon Mark Imprint: ${element}`,
            source: 'item',
            kind: 'trigger',
            cooldownTicks: 30,
            targeting: { shape: 'singleTarget' },
            activation: { onEvent: 'onSummonAttack' },
            effects: [{ kind: 'applyDebuff', debuffId: markId(element), stacks: 1 }],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 7: 召喚体属性付与 × 7 (passive、近似 stat-boost flag)
    // ---------------------------------------------------------------------------
    for (const element of ELEMENTS_7) {
        out[`item.common.summon-element-augment.${element}`] = {
            id: `item.common.summon-element-augment.${element}`,
            name: `Summon Element Augment: ${element}`,
            source: 'item',
            kind: 'passive',
            activation: {},
            effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
        };
    }
    // ---------------------------------------------------------------------------
    // archetype 8: thread aura 借用 × 3 (dark/light/earth、passive 弱体版)
    // ---------------------------------------------------------------------------
    for (const element of ['dark', 'light', 'earth']) {
        out[`item.common.thread-aura.${element}`] = {
            id: `item.common.thread-aura.${element}`,
            name: `Thread Aura: ${element}`,
            source: 'item',
            kind: 'passive',
            activation: { requiresPositionRelative: { reference: 'summon', maxDistance: 5 } },
            effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
        };
    }
    // ---------------------------------------------------------------------------
    // unique × 11 (§3)
    // ---------------------------------------------------------------------------
    out['item.unique.bone-tide.swarm'] = {
        id: 'item.unique.bone-tide.swarm',
        name: 'Bone Tide: Swarm',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(60),
        targeting: { shape: 'self' },
        activation: {},
        effects: [
            {
                kind: 'summon',
                spec: {
                    unitId: 'unit.skeleton.dark',
                    durationTicks: seconds(20),
                    count: 5,
                    maxConcurrentPerOwner: 10,
                },
            },
        ],
    };
    out['item.unique.golem-heart.guard'] = {
        id: 'item.unique.golem-heart.guard',
        name: 'Golem Heart: Guard',
        source: 'item',
        kind: 'passive',
        activation: { requiresPositionRelative: { reference: 'summon', maxDistance: 10 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.armor' }],
    };
    out['item.unique.angel-choir.heal-on-death'] = {
        id: 'item.unique.angel-choir.heal-on-death',
        name: 'Angel Choir: Heal on Death',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        targeting: { shape: 'allAllies' },
        activation: { onEvent: 'onSummonDied' },
        effects: [{ kind: 'heal', power: 0.1 }],
    };
    out['item.unique.fire-spirit.kamikaze'] = {
        id: 'item.unique.fire-spirit.kamikaze',
        name: 'Fire Spirit: Kamikaze',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        range: 5,
        targeting: { shape: 'circle', radius: 5, maxTargets: 8 },
        activation: { onEvent: 'onSummonDied' },
        effects: [{ kind: 'damage', element: 'fire', power: 2.0 }],
    };
    out['item.unique.thunder-familiar.chain-summon'] = {
        id: 'item.unique.thunder-familiar.chain-summon',
        name: 'Thunder Familiar: Chain Summon',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(60),
        targeting: { shape: 'self' },
        activation: {},
        effects: [
            {
                kind: 'summon',
                spec: {
                    unitId: 'unit.thunder-elemental.lightning',
                    durationTicks: seconds(30),
                    count: 2,
                    maxConcurrentPerOwner: 3,
                },
            },
        ],
    };
    out['item.unique.frost-bind.summon-immobile'] = {
        id: 'item.unique.frost-bind.summon-immobile',
        name: 'Frost Bind: Summon Immobile',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        targeting: { shape: 'singleTarget' },
        activation: { onEvent: 'onSummonAttack' },
        effects: [{ kind: 'applyDebuff', debuffId: 'freeze', stacks: 1 }],
    };
    out['item.unique.beast-pack.physical-leader'] = {
        id: 'item.unique.beast-pack.physical-leader',
        name: 'Beast Pack: Physical Leader',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(60),
        targeting: { shape: 'self' },
        activation: {},
        effects: [
            {
                kind: 'summon',
                spec: {
                    unitId: 'unit.beast.physical',
                    durationTicks: seconds(30),
                    count: 3,
                    maxConcurrentPerOwner: 5,
                },
            },
        ],
    };
    out['item.unique.threadmaster.dual-line'] = {
        id: 'item.unique.threadmaster.dual-line',
        name: 'Threadmaster: Dual Line',
        source: 'item',
        kind: 'passive',
        activation: { requiresPositionRelative: { reference: 'summon', maxDistance: 8 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.unique.summon-cap.expand'] = {
        id: 'item.unique.summon-cap.expand',
        name: 'Summon Cap: Expand',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.unique.necromancer-pact.death-amp'] = {
        id: 'item.unique.necromancer-pact.death-amp',
        name: 'Necromancer Pact: Death Amp',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    };
    out['item.unique.elemental-summoner.random-element'] = {
        id: 'item.unique.elemental-summoner.random-element',
        name: 'Elemental Summoner: Random Element',
        source: 'item',
        kind: 'active',
        cooldownTicks: seconds(60),
        targeting: { shape: 'self' },
        activation: {},
        effects: [
            {
                kind: 'summon',
                spec: {
                    unitId: 'unit.fire-spirit.fire',
                    durationTicks: seconds(30),
                    count: 1,
                    maxConcurrentPerOwner: 3,
                    randomElement: {
                        candidates: ['fire', 'ice', 'lightning', 'earth', 'light', 'dark'],
                        buffByElement: {
                            fire: 'mighty',
                            ice: 'composure',
                            lightning: 'charge',
                            earth: 'harden',
                            light: 'blessing',
                            dark: 'absorb',
                        },
                        buffDurationTicks: seconds(15),
                        buffTarget: 'caster',
                    },
                },
            },
        ],
    };
    return out;
})();
// =============================================================================
// Step 6.10 Summoner 補完 unique × 4 (`.context/c5c_role_audit.md` v1 §10.C)
//
// 空白軸 4 個 (3/5/7/9) に Summoner 向け unique 1 個ずつ追加 (role audit §2.3)。
// Summoner 合計: 55 → 59 個 (14% → 16%、目安範囲内)。
// 各 unique は「召喚体存命中」or「召喚体経由」を条件にした近似実装。
// =============================================================================
const SUMMONER_PATCH = {
    // 軸 3 補完: 召喚体生存中 placeable 半径 +X%
    'item.unique.summon-conjurer.placeable-link': {
        id: 'item.unique.summon-conjurer.placeable-link',
        name: 'Summon Conjurer: Placeable Link',
        source: 'item',
        kind: 'passive',
        activation: { requiresPositionRelative: { reference: 'summon', maxDistance: 10 } },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    },
    // 軸 5 補完: 採用 Tempo に応じて召喚体 atk 連動
    'item.unique.beastmaster.summon-behavior-link': {
        id: 'item.unique.beastmaster.summon-behavior-link',
        name: 'Beastmaster: Summon Behavior Link',
        source: 'item',
        kind: 'passive',
        activation: {
            requiresBehaviorRule: { category: 'tempo', ruleId: 'tempo_burst' },
        },
        effects: [{ kind: 'applyBuff', buffId: 'stat-boost.atk-bonus' }],
    },
    // 軸 7 補完: 召喚体死亡で caster HP 回復
    'item.unique.necromantic-cycle.summon-hp-pool': {
        id: 'item.unique.necromantic-cycle.summon-hp-pool',
        name: 'Necromantic Cycle: Summon HP Pool',
        source: 'item',
        kind: 'trigger',
        cooldownTicks: 30,
        targeting: { shape: 'self' },
        activation: { onEvent: 'onSummonDied' },
        effects: [{ kind: 'heal', power: 0.1 }],
    },
    // 軸 9 補完: 召喚体経由 kill で caster に streak 加算
    // (`kill_streak` reducer は ADR 0014 既存、本 unique は flag passive)
    'item.unique.kill-feast.summon-kill-feed': {
        id: 'item.unique.kill-feast.summon-kill-feed',
        name: 'Kill Feast: Summon Kill Feed',
        source: 'item',
        kind: 'passive',
        activation: {},
        effects: [{ kind: 'applyBuff', buffId: 'kill_streak' }],
    },
};
// =============================================================================
// ITEM_SKILLS export (catalogue 完成 = 全 ~405 件)
// =============================================================================
export const ITEM_SKILLS = {
    ...VANILLA_AXIS_6,
    ...KILLCHAIN_AXIS_9,
    ...ATTRIBUTE_AXIS_1,
    ...STATUS_AXIS_2,
    ...RESOURCE_AXIS_7,
    ...POSITION_AXIS_8,
    ...BEHAVIOR_AXIS_5,
    ...PLACEABLE_AXIS_3,
    ...SUMMON_AXIS_4,
    ...SUMMONER_PATCH,
};
export const ITEM_SKILL_IDS = Object.keys(ITEM_SKILLS);
//# sourceMappingURL=item-skills.js.map